import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import EnhancedMarkdown from "../../enhanced/EnhancedMarkdown";
import ScienceExpoDigitalHuman, { fetchDigitalHumanConfig } from "./ScienceExpoDigitalHuman";
import { ExpertAvatar } from "../../ExpertAvatarSDK/ExpertAvatar.js";
import {
  ensureSharedAudioContext,
  setDigitalHumanOutputGain,
} from "../../ExpertAvatarSDK/Player.js";
import FunASRClient from "../../../utils/FunASRClient";
import { DIGITAL_HUMAN_URL } from "../../../config/serviceConfig.js";
import { resolveBrandLogoUrl } from "../../../utils/brandLogo";
import { resolveBrandMediaUrl } from "../../../utils/brandTheme";
import { getLocalStorage, setLocalStorage } from "../../../utils/storageCache";
import debugLog from "../../../utils/debugLog.js";
import latencyTrace from "../../../utils/latencyTrace.js";
import ExpoDebugConsole from "./ExpoDebugConsole";
import "./ScienceExpoStage.css";

/* ─── 工具函数 ─── */
const getMessageText = (msg) => String(msg?.content || "").replace(/\s+/g, " ").trim();
const getAgentName  = (a) => a?.displayName || a?.name || a?.agentName || a?.agentId || "科学讨论角色";
const getAgentTitle = (a) => a?.role || a?.roleName || a?.description || a?.desc || "";
const getMemberName = (m) => m?.username || m?.displayName || m?.userId || "真人成员";
const getMemberTitle= (m) => m?.roleName || "";
const MALE_STUDENT_AVATARS = Array.from(
  { length: 7 },
  (_, index) => `/avatars/students/student-male-${String(index + 1).padStart(2, "0")}.png`
);
const FEMALE_STUDENT_AVATARS = {
  付雨诺: "/avatars/students/student-female-01.png",
  刘雨辰: "/avatars/students/student-female-02.png",
  许雅珊: "/avatars/students/student-female-03.png",
};
const DIGITAL_HUMAN_VOLUME_STORAGE_KEY = "groupchatDigitalHumanVolumePercent";
const DEFAULT_DIGITAL_HUMAN_VOLUME_PERCENT = 180;
const MIN_DIGITAL_HUMAN_VOLUME_PERCENT = 100;
const MAX_DIGITAL_HUMAN_VOLUME_PERCENT = 500;
const clampDigitalHumanVolumePercent = (value) => Math.min(
  MAX_DIGITAL_HUMAN_VOLUME_PERCENT,
  Math.max(MIN_DIGITAL_HUMAN_VOLUME_PERCENT, Number(value) || DEFAULT_DIGITAL_HUMAN_VOLUME_PERCENT)
);
const stableStringHash = (value) => {
  let hash = 2166136261;
  for (const char of String(value || "")) {
    hash ^= char.codePointAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};
const splitProjectName = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(.*)（创建者：(.+)）$/);
  if (!match) return { title: raw, creator: "" };
  return { title: match[1].trim(), creator: match[2].trim() };
};

/* 主持人虚拟卡片（始终存在） */
const HOST_AGENT = { agentId: "host", displayName: "主持人", role: "圆桌主持人", _isHost: true };

/* 早入队的最短 voice 长度。比这更短的多半是流到一半的残句或空壳，
 * 不值得为了 0.5s 冒"念出半句话"的险，交给定稿路径。 */
const MIN_EARLY_ENQUEUE_CHARS = 10;
/* 成员条里"某人刚说完话"的高亮时长。人类发言是一条瞬时消息，没有播放结束事件，
 * 只能给一个短窗口自动熄灭；下一位发言者出现时会提前清掉。 */
const MEMBER_SPEAKING_HIGHLIGHT_MS = 4000;
// 雷达按三类能力域连续排布，外围分区与下方分段条一一对应；不改变服务端九维评价口径。
const COMPETENCY_DIMENSIONS = ["科学观念", "科学思维", "探究实践", "具身认知", "AI态度", "元认知", "态度责任", "创造力", "批判性思维"];
const COMPETENCY_GROUPS = [
  { name: "科学探究", dimensions: ["科学观念", "科学思维", "探究实践"] },
  { name: "学习调控", dimensions: ["具身认知", "AI态度", "元认知"] },
  { name: "创新责任", dimensions: ["态度责任", "创造力", "批判性思维"] },
];

const RADAR_ROTATION = (-40 * Math.PI) / 180;
const radarPoint = (index, ratio, radius = 72, center = 130) => {
  const angle = -Math.PI / 2 + RADAR_ROTATION + (Math.PI * 2 * index) / COMPETENCY_DIMENSIONS.length;
  return `${(center + Math.cos(angle) * radius * ratio).toFixed(1)},${(center + Math.sin(angle) * radius * ratio).toFixed(1)}`;
};

const radarPolygon = (ratios) => ratios.map((ratio, index) => radarPoint(index, ratio)).join(" ");
const radarArc = (startAngle, endAngle, radius = 86, center = 130) => {
  const toPoint = (angle) => {
    const radians = (angle * Math.PI) / 180;
    return `${(center + Math.cos(radians) * radius).toFixed(1)} ${(center + Math.sin(radians) * radius).toFixed(1)}`;
  };
  return `M ${toPoint(startAngle)} A ${radius} ${radius} 0 0 1 ${toPoint(endAngle)}`;
};
const radarLabelPoint = (angle, radius = 108, center = 130) => {
  const radians = (angle * Math.PI) / 180;
  return {
    x: (center + Math.cos(radians) * radius).toFixed(1),
    y: (center + Math.sin(radians) * radius).toFixed(1),
  };
};
const radarAxisMax = (dimensions) => {
  const maxScore = Math.max(0, ...dimensions.map((dimension) => Number(dimension.score) || 0));
  return Math.max(4, Math.ceil(maxScore / 2) * 2 + 2);
};

/* ─── 花名册冻结 hook ─────────────────────────────────────────
 * 第一次 agents 和 phases 非空时计算一次，此后不再变化。
 * 顺序：主持人 → 所有阶段 activeAgents 并集 → agents 全集补充
 * ─────────────────────────────────────────────────────────── */
function useRoster(agents, phases, hostAgent = HOST_AGENT) {
  const rosterRef = useRef(null);

  return useMemo(() => {
    if (rosterRef.current) {
      rosterRef.current = [{ ...rosterRef.current[0], ...hostAgent }, ...rosterRef.current.slice(1)];
      return rosterRef.current;
    }
    if (!agents || agents.length === 0) return [hostAgent];

    const seen = new Set();
    const roster = [hostAgent];
    seen.add("host");

    // 所有阶段 activeAgents 并集（保持顺序）
    (phases || []).forEach((ph) => {
      (ph.activeAgents || []).forEach((id) => {
        if (seen.has(id)) return;
        const agent = agents.find((a) => (a.agentId || a.id) === id);
        if (agent) {
          roster.push(agent);
          seen.add(id);
        }
      });
    });

    // 补充 agents 全集中未收录的
    agents.forEach((a) => {
      const id = a.agentId || a.id;
      if (!id || seen.has(id)) return;
      roster.push(a);
      seen.add(id);
    });

    rosterRef.current = roster;
    return roster;
  }, [agents, phases, hostAgent]); // eslint-disable-line react-hooks/exhaustive-deps
}

/* ─── 花名册 ZIP/PNG 预加载 ─────────────────────────────────
 * 关键点：
 *   1. 必须先调 /api/digital-human/config 拿到真实 avatarPath（按 agentId 拼路径全是 404）。
 *   2. 模块级 Set 跨实例去重：StrictMode 双 mount + roster 引用变化会导致 useEffect
 *      被触发多次，每个实例都从空 Set 开始 → 同一 zip 被发起 N 次预加载请求。
 *      改为模块级 Set 后，整个会话内同一 path 只发起一次。
 *   3. fetchDigitalHumanConfig 也按 roleName 模块级缓存，避免重复 HTTP。
 * ─────────────────────────────────────────────────────────── */
const __preloadedAvatarPaths = new Set();
const __roleConfigCache = new Map(); // roleName -> Promise<config>
const ROLE_CONFIG_CACHE_TTL_MS = 30000;

const fetchDigitalHumanConfigCached = (roleName, workflowName, messageType) => {
  const key = `${roleName}|${workflowName}|${messageType}`;
  const cached = __roleConfigCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const p = fetchDigitalHumanConfig(roleName, workflowName, messageType);
  __roleConfigCache.set(key, {
    promise: p.catch((err) => {
      __roleConfigCache.delete(key);
      throw err;
    }),
    expiresAt: Date.now() + ROLE_CONFIG_CACHE_TTL_MS,
  });
  return __roleConfigCache.get(key).promise;
};

function useRosterPreload(roster, projectName, onProgress) {
  const onProgressRef = useRef(onProgress);
  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);

  useEffect(() => {
    if (!roster || roster.length === 0 || !DIGITAL_HUMAN_URL) return;
    let cancelled = false;

    (async () => {
      let newCount = 0;
      // 冷缓存时 zip 下载会主导启动等待（15 个约 120MB），所以单独报进度；
      // 热缓存时每个只要 0.02–0.05s，这段进度条一闪而过。
      let total = 0;
      let done = 0;
      const report = () => {
        if (!cancelled) onProgressRef.current?.({ done, total });
      };
      for (const agent of roster) {
        if (cancelled) break;
        const roleName = agent?.displayName || agent?.name || agent?.agentName || agent?.agentId;
        if (!roleName || agent?._isHost) continue;
        try {
          const cfg = await fetchDigitalHumanConfigCached(
            roleName, projectName || "AI科学圆桌", "science_expo"
          );
          if (cancelled || !cfg?.avatarPath) continue;
          total += 1;
          if (__preloadedAvatarPaths.has(cfg.avatarPath)) {
            // 已发起过（StrictMode 重跑 / roster 引用变化），算已完成
            done += 1;
            report();
            continue;
          }
          __preloadedAvatarPaths.add(cfg.avatarPath);
          newCount += 1;
          report();
          // PNG 预览
          const previewUrl = String(cfg.avatarPath).replace(/\.zip(\?.*)?$/i, ".png$1");
          const img = new Image();
          img.src = previewUrl.startsWith("http") ? previewUrl : `${DIGITAL_HUMAN_URL}${previewUrl}`;
          // ZIP 预热（preloadModel 内部会再做一层 force-cache）
          ExpertAvatar.preloadModel(cfg.avatarPath, DIGITAL_HUMAN_URL)
            .catch((err) => {
              debugLog.emit("PRELOAD", `❌ ${roleName}: ${err?.message}`, "error");
            })
            .finally(() => {
              done += 1;
              report();
            });
        } catch (err) {
          debugLog.emit("PRELOAD", `❌ ${roleName}: ${err?.message}`, "error");
        }
      }
      if (newCount > 0) {
        debugLog.emit("PRELOAD", `🎯 新增预加载 ${newCount} 个 zip（累计 ${__preloadedAvatarPaths.size}）`);
      }
    })();

    return () => { cancelled = true; };
  }, [roster, projectName]);
}

/* 注：曾尝试 useRosterWarmup 在隐藏 container 跑完整 getInstance 缓存 renderer 实例，
 * 但 SDK 内部 _canvas 是 static 共享，多 WebGLRenderer attach 同一 canvas 会触发
 * INVALID_OPERATION（program/uniform location 错位），画面错乱。已撤回。
 * 实测仅 zip HTTP 预下载就能把 loadAvatar 从 13–17s 缩到 1–3s（getInstance 的 await
 * 链路只到下载+解压+GLB，splat scene 是 fire-and-forget）。useRosterPreload 已覆盖。 */

/* 判断是否为阶段总结消息（不进入数字人发言通道，单独显示在右侧总结区） */
const isPhaseSummaryMessage = (m) =>
  m?.messageType === "phase_summary" ||
  m?.senderId === "phase_summary_agent" ||
  m?.agentId === "phase_summary_agent" ||
  (m?.senderType === "system" && m?.content?.includes("阶段总结"));

const isPhaseSummaryStream = (agentId, data) =>
  agentId === "phase_summary_agent" || data?.agentId === "phase_summary_agent";

/* 从流式内容里取"已闭合"的 <voice> 正文。
 * 必须坚持要求闭合标签：流到一半时内容可能是 "<voi"、"<voice>今天我" 这类残缺片段，
 * 拿去合成会念出乱码或半句话。闭合了才说明这句话的可朗读部分已经完整。 */
const extractClosedVoice = (raw) => {
  const matched = String(raw || "").match(/<voice>\s*([\s\S]*?)\s*<\/voice>/i);
  return matched ? matched[1].trim() : "";
};

const extractEmbodiedVoice = (raw) => {
  const closed = extractClosedVoice(raw);
  if (closed) return closed;
  const text = String(raw || "");
  if (/<voice>/i.test(text) && !/<\/voice>/i.test(text)) return "";
  return text
    .replace(/<detail>[\s\S]*?<\/detail>/ig, "")
    .replace(/<\/?voice>/ig, "")
    .replace(/<gesture>[\s\S]*?<\/gesture>/ig, "")
    .trim();
};

const parseExpoVoiceDetail = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return { voice: "", detail: "", hasDetail: false };

  const voiceMatch = text.match(/<voice>\s*([\s\S]*?)\s*<\/voice>/i);
  const detailMatch = text.match(/<detail>\s*([\s\S]*?)\s*<\/detail>/i);

  // 流式输出中标签尚未闭合时，不要把半截标签当字幕/TTS 读出来。
  if (!voiceMatch && /<voice>/i.test(text)) {
    return { voice: "", detail: "", hasDetail: false };
  }

  if (voiceMatch || detailMatch) {
    const voice = (voiceMatch?.[1] || "").trim();
    const detail = (detailMatch?.[1] || "").trim();
    const fallbackVoice = text
      .replace(/<detail>[\s\S]*?<\/detail>/ig, "")
      .replace(/<\/?voice>/ig, "")
      .trim();
    return {
      voice: voice || getMessageText({ content: fallbackVoice }).slice(0, 80),
      detail: detail || text.replace(/<voice>[\s\S]*?<\/voice>/ig, "").trim(),
      hasDetail: !!detail,
    };
  }

  return {
    voice: getMessageText({ content: text }),
    detail: text,
    hasDetail: false,
  };
};

/* ─── 当前发言状态推导 ─── */
const deriveExpoState = ({ agents, members, messages, streamingContent, phaseLabel }) => {
  const streamingEntries = Object.entries(streamingContent || {})
    .filter(([agentId, data]) => !isPhaseSummaryStream(agentId, data));
  if (streamingEntries.length > 0) {
    const [agentId, data] = streamingEntries[0];
    const parsed = parseExpoVoiceDetail(data.content || "");
    const agent = agents.find((a) => a.agentId === agentId || a.id === agentId) || {
      agentId, displayName: data.agentName,
    };
    return {
      activeSpeaker: agent,
      activeSpeakerType: "agent",
      activeSpeechText: parsed.voice,
      activeSpeechDetail: parsed.detail,
      hasSpeechDetail: parsed.hasDetail,
      rawSpeechText: data.content || "",
      isStreaming: true,
    };
  }

  const recent = [...(messages || [])].reverse().find(
    (m) => !isPhaseSummaryMessage(m) &&
      (m.senderType === "agent" || m.senderType === "host" || m.senderType === "student")
  );
  if (recent) {
    if (recent.senderType === "student") {
      const member = members.find(
        (m) => m.userId === recent.senderId || m.username === recent.senderName || m.displayName === recent.senderName
      ) || { userId: recent.senderId, username: recent.senderName, roleName: recent.senderRole };
      return { activeSpeaker: member, activeSpeakerType: "member", activeSpeechText: getMessageText(recent), isStreaming: false };
    }
    // host / agent
    const hostAgent = recent.senderType === "host"
      ? { ...HOST_AGENT, displayName: recent.senderName || "主持人" }
      : null;
    const agent = hostAgent
      || agents.find((a) => a.agentId === recent.senderId || a.id === recent.senderId || getAgentName(a) === recent.senderName)
      || { agentId: recent.senderId, displayName: recent.senderName };
    const parsed = parseExpoVoiceDetail(recent.content || "");
    return {
      activeSpeaker: agent,
      activeSpeakerType: "agent",
      activeSpeechText: parsed.voice,
      activeSpeechDetail: parsed.detail,
      hasSpeechDetail: parsed.hasDetail,
      rawSpeechText: recent.content || "",
      isStreaming: false,
    };
  }

  return { activeSpeaker: null, activeSpeakerType: "none", activeSpeechText: "", activeSpeechDetail: "", hasSpeechDetail: false, isStreaming: false };
};

/* ─── 阶段信息 ─── */
const findPhaseIndex = (phases, currentPhase, phaseLabel) => {
  const idx = (phases || []).findIndex(
    (p) => p.phaseId === currentPhase || p.id === currentPhase || p.name === phaseLabel || p.title === phaseLabel
  );
  return idx >= 0 ? idx : 0;
};

/* 从消息列表取最近的阶段总结（markdown 文本） */
const findLatestPhaseSummary = (messages) => {
  if (!messages || messages.length === 0) return "";
  const summaryMsg = [...messages].reverse().find(isPhaseSummaryMessage);
  return summaryMsg ? String(summaryMsg.content || "").trim() : "";
};

const parseExpoPhaseSummary = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return { display: "", full: "", hasMarkedDisplay: false };

  // 1) 标准 XML 标签 <SUMMARY>...</SUMMARY>（后端 prompt 强制要求）
  const xmlRe = /<\s*SUMMARY\s*>\s*([\s\S]*?)\s*<\s*\/\s*SUMMARY\s*>/i;
  const xmlMatch = text.match(xmlRe);
  if (xmlMatch) {
    const display = xmlMatch[1].trim();
    const full = text.replace(xmlRe, "").trim();
    return { display, full: full || text, hasMarkedDisplay: true };
  }

  // 2) 兼容 LLM 把括号写成中文/全角/方括号变体：[SUMMARY]、【SUMMARY】、《SUMMARY》
  const altRe = /[\[【《<]\s*SUMMARY\s*[\]】》>]\s*([\s\S]*?)\s*[\[【《<]\s*\/\s*SUMMARY\s*[\]】》>]/i;
  const altMatch = text.match(altRe);
  if (altMatch) {
    const display = altMatch[1].trim();
    const full = text.replace(altRe, "").trim();
    return { display, full: full || text, hasMarkedDisplay: true };
  }

  // 3) 兜底：找"群聊短总结"小标题（- / # / 【】 / [] 包裹皆可），抓到下一个小标题之前的内容
  const headingRe = /(?:^|\n)\s*(?:#{1,6}\s*|[-*]\s*|【|\[)?\s*群聊短总结\s*(?:】|\])?\s*[:：]?\s*\n?([\s\S]*?)(?=\n\s*(?:#{1,6}\s+|[-*]\s+|【|\[)?\s*(?:阶段结论|已明确内容|未决问题|最大风险|下一步验证任务|是否建议进入下一阶段))/i;
  const headingMatch = text.match(headingRe);
  if (headingMatch) {
    const display = headingMatch[1].trim().replace(/^[-*]\s*/gm, "").slice(0, 180);
    return { display, full: text, hasMarkedDisplay: true };
  }

  // 4) 实在没有标记 → 取前 5 行非空 + 截 180 字
  const plainLines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^#{1,6}\s+/.test(line))
    .slice(0, 5);
  let display = plainLines.join("\n");
  if (display.length > 180) display = `${display.slice(0, 180)}…`;
  return { display: display || text.slice(0, 180), full: text, hasMarkedDisplay: false };
};

/* 从阶段小结里抽出结尾那句话，交给主持人念。
 *
 * 小结本身是要看的（要点列表 + mermaid 图），不适合朗读；但它末尾通常有一句
 * 面向学生的问句（"总结已经完成，是进入下一个阶段？还是继续讨论？"）。
 * 不念这句的话，现场就是字幕卡在"主持人正在点人……"直到 30 秒兜底超时。 */
const SUMMARY_CLOSING_FALLBACK = "本阶段小结已经生成，是进入下一个阶段，还是继续讨论？";
const extractSummaryClosing = (raw) => {
  const text = String(raw || "")
    .replace(/```[\s\S]*?```/g, "")          // mermaid / 代码块
    .replace(/<\/?[A-Za-z][^>]*>/g, "")      // <SUMMARY> 等标签
    .replace(/[*_`#>]/g, "");
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    // 要点行（-、*、·、1. 开头）是给眼睛看的，不念
    .filter((line) => !/^([-*·•]|\d+[.、])\s*/.test(line));
  // 从后往前找第一句问句：结尾那句几乎总是在问学生下一步怎么走
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    if (line.length <= 80 && /[?？]/.test(line)) return line;
  }
  const last = lines[lines.length - 1];
  if (last && last.length <= 80) return last;
  return SUMMARY_CLOSING_FALLBACK;
};

/* ─── 主组件 ─── */
const ScienceExpoStage = ({
  projectName,
  scriptMode = "",
  agents = [],
  messages = [],
  streamingContent = {},
  currentPhase,
  phaseLabel,
  phases = [],
  activeAgentIds = [],
  status,
  isDisconnected,
  onSendMessage,
  onSaveArchive,
  currentUser,
  brandThemeStyle = null,
  members = [],
  myUserId = "",
  robotBind = null,
  onReleaseRobot,
  queueStatusText = "",
  layoutVariant = "classic",
  competencyProfile = null,
  competencyHistory = [],
  silentTurnNotice = null,
  speakingMemberIds = null,
  onSpeakingStateChange = null,
  onClose,
}) => {
  const { t } = useTranslation();
  const digitalHumanEnabled = true;
  const isRecordingScriptMode = scriptMode === "recording";
  const projectTitleParts = splitProjectName(projectName);
  const projectWorkflowName = projectTitleParts.title || projectName || "AI科学圆桌";
  const competencyDimensions = useMemo(() => {
    const source = competencyProfile?.dimensions || [];
    return COMPETENCY_DIMENSIONS.map((name) => source.find((dimension) => dimension.name === name) || {
      name, score: 0, delta: 0, evidence: [],
    });
  }, [competencyProfile]);
  const competencyHasData = Boolean(competencyProfile?.evaluatedSeq);
  const competencyAxisMax = useMemo(() => radarAxisMax(competencyDimensions), [competencyDimensions]);
  const competencyRatios = useMemo(
    () => competencyDimensions.map((dimension) => Math.min(1, (Number(dimension.score) || 0) / competencyAxisMax)),
    [competencyDimensions, competencyAxisMax]
  );
  const competencyGroups = useMemo(
    () => COMPETENCY_GROUPS.map((group) => ({
      ...group,
      dimensions: group.dimensions.map((name) => competencyDimensions.find((dimension) => dimension.name === name)),
    })),
    [competencyDimensions]
  );
  const studentRounds = useMemo(
    () => messages.filter((message) => message?.senderType === "student").length,
    [messages]
  );
  /* 同一房间所有客户端按稳定身份键排序后分配头像：
   * 三位已知女生固定使用 3 张女生图；其他用户名先按身份哈希选男生图，
   * 碰撞时向后找空槽，7 张用完才循环。既尽量不重复，也避免成员晚到时
   * 让已有成员头像整体换位。展示顺序仍沿用 members。 */
  const memberAvatarMap = useMemo(() => {
    const result = new Map();
    const usedMaleSlots = new Set();
    const stableMembers = [...members].sort((a, b) => {
      const aKey = String(a?.userId || getMemberName(a)).trim();
      const bKey = String(b?.userId || getMemberName(b)).trim();
      return aKey.localeCompare(bKey, "zh-CN");
    });
    stableMembers.forEach((member) => {
      const name = String(getMemberName(member)).trim();
      const roleKey = `member:${member?.userId || name}`;
      const femaleAvatar = FEMALE_STUDENT_AVATARS[name];
      if (femaleAvatar) {
        result.set(roleKey, femaleAvatar);
        return;
      }
      const preferredSlot = stableStringHash(member?.userId || name) % MALE_STUDENT_AVATARS.length;
      let slot = preferredSlot;
      if (usedMaleSlots.size < MALE_STUDENT_AVATARS.length) {
        for (let offset = 0; offset < MALE_STUDENT_AVATARS.length; offset += 1) {
          const candidate = (preferredSlot + offset) % MALE_STUDENT_AVATARS.length;
          if (!usedMaleSlots.has(candidate)) {
            slot = candidate;
            break;
          }
        }
      }
      usedMaleSlots.add(slot);
      result.set(roleKey, MALE_STUDENT_AVATARS[slot]);
    });
    return result;
  }, [members]);
  const recentEvidence = useMemo(
    () => [...competencyHistory].slice(-3).reverse().map((profile) => ({
      ...profile,
      gains: (profile.dimensions || []).filter((dimension) => (Number(dimension.delta) || 0) > 0),
    })),
    [competencyHistory]
  );
  const [hostDisplayName, setHostDisplayName] = useState(HOST_AGENT.displayName);
  const [hostDigitalHumanConfig, setHostDigitalHumanConfig] = useState(null);
  const [hostConfigReady, setHostConfigReady] = useState(false);
  const hostAgent = useMemo(
    () => ({
      ...HOST_AGENT,
      displayName: hostDisplayName || HOST_AGENT.displayName,
      digitalHumanConfig: hostDigitalHumanConfig,
    }),
    [hostDisplayName, hostDigitalHumanConfig]
  );

  useEffect(() => {
    let cancelled = false;
    setHostConfigReady(false);
    setHostDigitalHumanConfig(null);
    fetchDigitalHumanConfigCached(HOST_AGENT.displayName, projectWorkflowName, "science_expo")
      .then((cfg) => {
        const resolvedName = String(cfg?.name || "").trim();
        if (cancelled) return;
        if (resolvedName) setHostDisplayName(resolvedName);
        setHostDigitalHumanConfig(cfg || null);
        setHostConfigReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setHostDisplayName(HOST_AGENT.displayName);
        setHostDigitalHumanConfig(null);
        setHostConfigReady(true);
      });
    return () => { cancelled = true; };
  }, [projectWorkflowName]);
  /* currentUser ref：入队 useEffect 仅依赖 messages/agents/projectName，
     用 ref 旁路 currentUser，避免每次 props 抖动重跑入队逻辑 */
  const [isVoiceRecording, setIsVoiceRecording]   = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [voiceTranscript, setVoiceTranscript]     = useState("");
  const [voiceError, setVoiceError]               = useState("");
  const [displaySpeaker, setDisplaySpeaker]       = useState(null);
  const [speechDetailOpen, setSpeechDetailOpen]   = useState(false);
  /* centerLock：持久化当前发言中角色，只在新发言者出现时更换，不因"无人发言"而清空 */
  const [centerLock, setCenterLock]               = useState(null);
  /* 成员条的发言高亮不能跟着 centerLock 走：centerLock 的持久化是为了中央数字人
   * 不因"此刻没人说话"而变空，但成员条要表达的是"谁正在说"。人类发言没有播放
   * 结束事件，跟着 centerLock 就会一路亮到下一位发言者出现——AI 思考那几秒里，
   * 早就说完的人还挂着高亮。这里单独记一个到点自动熄灭的 key。 */
  const [recentMemberKey, setRecentMemberKey]     = useState("");
  const recentMemberTimerRef = useRef(null);
  useEffect(() => {
    setCenterLock((prev) => {
      if (prev?.type !== "agent" || !prev.speaker?._isHost) return prev;
      const nextName = hostDisplayName || HOST_AGENT.displayName;
      if (getAgentName(prev.speaker) === nextName) return prev;
      return { ...prev, speaker: { ...prev.speaker, displayName: nextName } };
    });
  }, [hostDisplayName]);
  const [isFullscreen, setIsFullscreen]           = useState(false);
  const [digitalHumanVolumePercent, setDigitalHumanVolumePercent] = useState(() => (
    clampDigitalHumanVolumePercent(getLocalStorage(DIGITAL_HUMAN_VOLUME_STORAGE_KEY))
  ));
  useEffect(() => {
    setDigitalHumanOutputGain(digitalHumanVolumePercent / 100);
    setLocalStorage(DIGITAL_HUMAN_VOLUME_STORAGE_KEY, String(digitalHumanVolumePercent));
  }, [digitalHumanVolumePercent]);
  const orgBrand = useMemo(() => {
    const branding = currentUser?.branding;
    const title = String(branding?.title || "").trim();
    const schoolName = String(branding?.schoolName || "").trim();
    return {
      logoUrl: resolveBrandLogoUrl(branding?.logoUrl),
      title: title || t("app_title"),
      schoolName,
      schoolNameType: branding?.schoolNameType === "image" ? "image" : "text",
      schoolNameSrc: branding?.schoolNameType === "image" ? resolveBrandMediaUrl(schoolName) : "",
      showPlatformMark: Boolean(branding && String(branding.logoUrl || "").trim()),
    };
  }, [currentUser?.branding, t]);
  /* 自己说话状态机：
   *   idle        — 未按下
   *   arming      — 已按下，等待 ASR 真正 onstart（WebSocket 连 + 麦克风权限 + AudioContext 启动）
   *   recording   — ASR 已经在录音，可以放心说话
   *   recognizing — 识别中
   *   waiting     — 等 AI 回应
   * 注：必须区分 arming/recording，否则按下瞬间还没真正录音时用户就开口，前段语音会被吞掉，
   *     这是空格键比鼠标识别效果差很多的根本原因。 */
  const [selfSpeechPhase, setSelfSpeechPhase]     = useState("idle");
  /* AI 过渡态：waiting → 收到回应文本，但数字人还没开口（音频/形象未到）。
   * 此期间字幕显示"AI 正在准备发言…"，避免短暂闪现上一位 AI 的旧字幕。 */
  const [aiTransitioning, setAiTransitioning]     = useState(false);
  /* 主持人静默轮的明确引导。短时接管字幕区，避免学生停留在"等待回应"状态。 */
  const [silentGuide, setSilentGuide]             = useState(null);
  /* 中央数字人加载状态：null | { reason } —— 用于切换不同 zip 时显示过渡卡 */
  const [centerLoading, setCenterLoading]         = useState(null);
  /* 数字人发言队列（agent/host）：head 为正在/即将发言者；tail 为排队中。
   * 多个 Agent 几乎同时返回时，由队列保证"前一个说完再换下一个"，
   * 避免 centerAgentEntry 立即切换造成打断。 */
  const [speechQueue, setSpeechQueue]             = useState([]);
  /* 启动期阻塞式全量预载。
   *   pending  — roster 还没到位
   *   running  — 正在逐个挂，遮罩显示进度，此期间压住队列不让任何人开口
   *   done     — 全部就绪
   *   skipped  — 预载卡死超时兜底，剩下的角色退回边演示边挂
   * 实测（热缓存，15 角色）：每个约 2s，全部约 30s；解析 0.18–0.39s、
   * splat 构建 1.4–1.9s，且不随已挂载数量增长——所以可以全部常驻，不需要 LRU。 */
  const [preloadPhase, setPreloadPhase]           = useState("pending");
  const [preloadDone, setPreloadDone]             = useState(0);
  const [preloadTotal, setPreloadTotal]           = useState(0);
  const [preloadCurrent, setPreloadCurrent]       = useState("");
  const [zipProgress, setZipProgress]             = useState({ done: 0, total: 0 });
  /* 开场闸门：资源就绪后不自动开演，停在"就绪待开场"等演示者点「开始」。
   * 把"开场时刻"从加载速度手里拿回来——预载耗时几十秒到两分钟不可控，
   * 遮罩一撤主持人就开讲，演示者往往还在接投影、等观众安静。 */
  const [stageStarted, setStageStarted]           = useState(false);
  /* 进度卡上的「开始后进入全屏」。默认勾选：演示场景多数要投全屏。
   * 勾选本身不进全屏；点「开始」时在同一用户手势里申请，否则浏览器会拒。 */
  const [startInFullscreen, setStartInFullscreen] = useState(true);
  const preloadSkipRef = useRef(false);
  const queuedKeysRef = useRef(new Set());
  /* </voice> 闭合时提前入队的登记表：senderId -> { key, voice }。
   * 定稿那一轮据此判断"这条已经入过队了，只需补 detail"。
   * 用户打断时刻意不清空，好让被打断那条的定稿消息不再重新入队。 */
  const earlyQueuedRef = useRef(new Map());
  const waitTimerRef = useRef(null);
  const silentGuideTimerRef = useRef(null);
  const summaryScrollRef = useRef(null);
  // 进入 waiting 那一刻的 activeSpeechText 基线：只有当文本真正改变才视为收到 AI 回应
  const waitBaselineRef = useRef("");
  // expoState 的最新引用（rec.onresult 闭包内同步读）
  const expoStateRef = useRef(null);
  // 中央数字人的命令式控制句柄（由子组件注册）：{ interrupt }
  // P3 多数字人共存：N 个 spotlight 实例每人一个 ref，interrupt 时遍历调
  const spotlightControlRefsRef = useRef(new Map());
  const getSpotlightControlRef = useCallback((key) => {
    let ref = spotlightControlRefsRef.current.get(key);
    if (!ref) {
      ref = { current: null };
      spotlightControlRefsRef.current.set(key, ref);
    }
    return ref;
  }, []);
  const interruptAllSpotlights = useCallback(() => {
    for (const ref of spotlightControlRefsRef.current.values()) {
      try { ref.current?.interrupt?.(); } catch (_) {}
    }
  }, []);
  const recognitionRef   = useRef(null);
  const lastVoiceSentRef = useRef("");
  const stageRootRef = useRef(null);
  const humanStripRef = useRef(null);

  const clearSilentGuide = useCallback(() => {
    if (silentGuideTimerRef.current) {
      clearTimeout(silentGuideTimerRef.current);
      silentGuideTimerRef.current = null;
    }
    setSilentGuide(null);
  }, []);

  const releaseFullscreenButtonFocus = useCallback(() => {
    const active = document.activeElement;
    if (active?.closest?.(".expo-icon-btn")) {
      active.blur();
    }
    stageRootRef.current?.focus?.({ preventScroll: true });
  }, []);

  /* 全屏状态监听 */
  useEffect(() => {
    const onFsChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      // 点全屏后焦点会停在按钮上，空格会被当成“点击按钮”而不是按住说话
      if (fs) {
        releaseFullscreenButtonFocus();
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, [releaseFullscreenButtonFocus]);

  /* 花名册：冻结的虚拟角色列表 */
  const virtualRoster = useRoster(agents, phases, hostAgent);

  /* 预加载花名册资源（zip → HTTP 缓存，是当前性能优化的主要来源） */
  useRosterPreload(virtualRoster, projectWorkflowName, setZipProgress);

  const expoState = useMemo(
    () => deriveExpoState({ agents, members, messages, streamingContent, phaseLabel }),
    [agents, members, messages, streamingContent, phaseLabel]
  );
  expoStateRef.current = expoState;
  const phaseIndex = findPhaseIndex(phases, currentPhase, phaseLabel);
  const currentPhaseObj = phases[phaseIndex] || null;
  const stageActiveAgentKeySet = useMemo(() => {
    const ids = (activeAgentIds && activeAgentIds.length > 0)
      ? activeAgentIds
      : (currentPhaseObj?.activeAgents || []);
    const set = new Set((ids || []).map((id) => String(id)).filter(Boolean));
    // 主持人始终参与现场调度，避免被误显示为非本阶段角色。
    set.add("host");
    return set;
  }, [activeAgentIds, currentPhaseObj]);
  const hasStageActiveInfo = stageActiveAgentKeySet.size > 1;

  /* P3 lazy mount：spotlight 内 N 个 ExpertAvatar 不再启动期全部 mount。
   * 启动只 mount "主持人 + 当前阶段活跃角色"，其他角色被入队 / 切到当前阶段时才 mount。
   * 原因：12 角色串行 loadAvatar（互斥锁 + 600ms 间隔 + GPU 上传逐渐变慢）
   *      累计 ~170s，排在后面的角色被点名时还没 GPU 就绪 → 40s 起开口延迟。
   * mountedAgentIds 一旦加入就不移除（已 mount 的实例长期驻留，下次切换零成本）。 */
  const [mountedAgentIds, setMountedAgentIds] = useState(() => new Set(["host"]));
  const requestMountAgent = (idLike) => {
    const id = String(idLike || "").trim();
    if (!id) return;
    setMountedAgentIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };
  /* 注：曾有一段"首阶段活跃角色批量 mount"。现在启动预载会把整个 roster 串行挂完，
   * 它变成纯粹的坏处——SDK getInstance 互斥锁是 FIFO，一次提交 N 个 mount 会让
   * 后面的角色排长队（实测排队时间涨到 85s），被点名的角色反而更慢。已删。 */

  /* 后台续 mount：首阶段 mount 触发后，利用第一阶段 ~10 分钟的空闲时间，
   * 把 roster 里其余角色逐个 mount 上，避免后续阶段切换/被点名时再排队等。
   *
   * 这件事必须跑完：没预挂的角色被点到时只能现场加载，而那次加载整个卡在
   * 关键路径上（drainQueue 要等 visualReady 才 speak），实测角色出声从 5.3s 涨到 11–15s。
   *
   * 节奏由两个闸门决定：
   *   1) 台上安静（waitForStageIdle，判定见 stageBusy）。空闲加载只要 0.18s，
   *      与发言或录音重叠则涨到 8–19s，涨幅几乎正比于重叠秒数。
   *      唯一放行的忙时段是"等群聊调度"，那 6–8 秒客户端主线程是空的。
   *   2) 上一个形象彻底加载完（waitForLoadIdle）—— 含未 await 的 splat 构建，
   *      避免固定间隔往锁队列里堆积（早先排队时间一路涨到 85s）。
   *   - 跳过已经 mount 的（首阶段角色 / 已被入队过的）
   * 任意时刻用户切到该角色，已经 mount 的实例 setActive(true) 立即露脸。 */
  // 镜像最新 mountedAgentIds / virtualRoster 给 backfill 闭包，避免 stale closure
  const mountedAgentIdsRefForBackfill = useRef(mountedAgentIds);
  useEffect(() => { mountedAgentIdsRefForBackfill.current = mountedAgentIds; }, [mountedAgentIds]);
  const virtualRosterRefForBackfill = useRef(virtualRoster);
  useEffect(() => { virtualRosterRefForBackfill.current = virtualRoster; }, [virtualRoster]);
  /* 什么时候不许启动新的形象加载。
   *
   * 加载对主线程竞争极度敏感：zip 下载只占 0.35–0.49s，剩下 8–19s 全是解压和解析，
   * 而且这个数几乎等于"期间有多少秒在录音或播放"（最干净的一例是按住 18.45s、
   * 加载 18.72s）。说明 SDK 的解压/解析在主线程上做，被别的活动挤到几乎不推进。
   *
   * 两个必须避开的时段：
   *   - 有数字人在出声：RAF 渲染 + blendshapes 入帧 + 音频调度
   *   - 观众在录音：FunASRClient 用的是 ScriptProcessorNode，onaudioprocess 跑在
   *     主线程上。主线程被占住超过一个缓冲区时长（4096 帧 ≈ 85ms）浏览器就直接
   *     丢帧，表现为识别结果缺字或串味——现场看就是"AI 听错了观众的问题"。
   *
   * 但 waiting（等群聊调度那 6–8 秒）必须放行：那段客户端只是在等 WebSocket，
   * 主线程空着，是整场唯一稳定存在的加载窗口。早先把它也算成忙，
   * 结果后台续 mount 被饿死 166 秒只跑完 1 个角色，被点到的角色只能现场加载，
   * 角色出声从 5.3s 涨到 11–15s。 */
  const stageBusyRef = useRef(false);
  /* 资源就绪但演示者还没点「开始」。定义在这里而不是跟 holdStage 放一起，
   * 是因为 stageBusy 要用它，而 const 有 TDZ，不能引用后面才声明的量。 */
  const waitingForStart = !isRecordingScriptMode && !stageStarted;
  /* ⚠️ 闸门期间必须算"不忙"：开场白已经躺在 speechQueue 里，但没人在播、
   * 也没人在录音，主线程是空的，正是最好的加载窗口。若按队列长度判定为忙，
   * 超时兜底解除预载遮罩后剩余角色的续 mount 会一直等到点「开始」才动。 */
  const stageBusy = !waitingForStart
    && (speechQueue.length > 0
      || (selfSpeechPhase !== "idle" && selfSpeechPhase !== "waiting"));
  useEffect(() => { stageBusyRef.current = stageBusy; }, [stageBusy]);
  /* 启动期阻塞式全量预载：进场先把 roster 全部挂完再开演。
   *
   * 每次等上一个形象彻底建完再推下一个，全程串行——并发提交会让 FIFO 互斥锁
   * 排起长队，早先实测排队时间一路涨到 85s。
   *
   * 遮罩期间和"就绪待开场"期间 stageBusy 恒为假（没人说话），所以
   * waitForStageIdle 只在超时兜底解除遮罩、且演示已经开始之后才真正起作用：
   * 那时剩下的角色退回边演示边挂。
   *
   * StrictMode 友好：cleanup → cancelled=true + 杀 timer；下次 mount 干净重启。 */
  useEffect(() => {
    if (isRecordingScriptMode) {
      debugLog.emit("RENDER", "🎬 录课模式：跳过启动预载");
      setPreloadPhase("skipped");
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let batchStart = 0;
    let mountedCount = 0;
    let doneAnnounced = false;
    const sleep = (ms) => new Promise((res) => { timer = setTimeout(res, ms); });
    /* 等台上连续安静一段时间。要求"连续"是因为两次发言之间常有 1–2 秒空档，
     * 抓着那个空档起一次 20 秒的加载，等于精准撞上下一位发言。 */
    const waitForStageIdle = async () => {
      let quietSince = 0;
      const t0 = Date.now();
      let announced = false;
      while (!cancelled) {
        if (stageBusyRef.current) {
          quietSince = 0;
          if (!announced) {
            announced = true;
            debugLog.emit("RENDER", "⏸️ 台上有发言，续 mount 暂停");
          }
        } else {
          if (!quietSince) quietSince = Date.now();
          if (Date.now() - quietSince >= 1500) {
            if (announced) {
              debugLog.emit("RENDER", `▶️ 台上转静（等了 ${((Date.now() - t0) / 1000).toFixed(1)}s），继续续 mount`);
            }
            return true;
          }
        }
        await sleep(400);
      }
      return false;
    };
    /* roster 分批到达：先只有主持人，成员配置拉回来后才 push 进去。
     * 遮罩要显示"共几个"，所以先等它稳定下来再开工。 */
    const waitForRosterSettled = async () => {
      let lastLen = -1;
      let stableSince = 0;
      while (!cancelled) {
        const len = (virtualRosterRefForBackfill.current || []).length;
        if (len > 1 && len === lastLen) {
          if (!stableSince) stableSince = Date.now();
          if (Date.now() - stableSince >= 1000) return len;
        } else {
          stableSince = 0;
        }
        lastLen = len;
        await sleep(300);
      }
      return 0;
    };
    /* "扫一遍没找到活干"不等于挂完了——roster 可能还在增长，也可能后续阶段
     * 才出现新角色，所以绝不能就此永久退出。早先用固定 10s 延迟躲 roster 竞态，
     * 但 roster 慢一点就会静默放弃整个续 mount。 */
    const findNextUnmounted = () => {
      const roster = virtualRosterRefForBackfill.current || [];
      const mounted = mountedAgentIdsRefForBackfill.current;
      for (const agent of roster) {
        const idStr = String(agent?.agentId || agent?.id || getAgentName(agent) || "");
        const nameStr = getAgentName(agent);
        if (!idStr && !nameStr) continue;
        if (mounted.has(idStr) || mounted.has(nameStr)) continue;
        return { agent, idStr, nameStr };
      }
      return null;
    };
    const tick = async () => {
      const rosterLen = await waitForRosterSettled();
      if (cancelled || !rosterLen) return;
      // 主持人在首屏就自己挂上了（它要第一个说话），不计入待挂总数。
      let pendingTotal = 0;
      for (const agent of virtualRosterRefForBackfill.current || []) {
        const idStr = String(agent?.agentId || agent?.id || getAgentName(agent) || "");
        if (!mountedAgentIdsRefForBackfill.current.has(idStr)
          && !mountedAgentIdsRefForBackfill.current.has(getAgentName(agent))) pendingTotal += 1;
      }
      setPreloadTotal(pendingTotal);
      setPreloadPhase((prev) => (prev === "pending" ? "running" : prev));
      debugLog.emit("RENDER", `🎬 启动预载：roster ${rosterLen} 个，待挂 ${pendingTotal} 个`);

      while (!cancelled) {
        const next = findNextUnmounted();
        if (!next) {
          if (!doneAnnounced && mountedCount > 0) {
            doneAnnounced = true;
            const total = ((Date.now() - batchStart) / 1000).toFixed(1);
            const avg = ((Date.now() - batchStart) / 1000 / mountedCount).toFixed(2);
            debugLog.emit("RENDER", `✅ 预载完成：${mountedCount} 个共 ${total}s（均 ${avg}s/个）`);
          }
          setPreloadPhase((prev) => (prev === "running" ? "done" : prev));
          setPreloadDone((prev) => Math.max(prev, pendingTotal));
          setPreloadCurrent("");
          await sleep(2000);
          continue;
        }
        doneAnnounced = false;
        // 遮罩还在时不必等台上安静——台上本来就没人。跳过之后演示已经开始，
        // 剩下的角色必须重新遵守闸门，否则加载会压在发言或录音上。
        if (preloadSkipRef.current && !(await waitForStageIdle())) return;
        if (!batchStart) batchStart = Date.now();
        setPreloadCurrent(next.nameStr);
        requestMountAgent(next.idStr || next.nameStr);
        mountedCount += 1;
        debugLog.emit("RENDER", `🔄 续 mount：${next.nameStr}（${mountedCount}/${pendingTotal}）`);
        // 该角色的 loadAvatar 是 mount 之后异步发起的，先给它排上队的时间，
        // 否则 waitForLoadIdle 会在它入队前就判定为空闲，退化成固定间隔。
        await sleep(1200);
        if (cancelled) return;
        await ExpertAvatar.waitForLoadIdle();
        if (cancelled) return;
        setPreloadDone(mountedCount);
        await sleep(preloadSkipRef.current ? 1000 : 0);
      }
    };
    timer = setTimeout(tick, 300);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isRecordingScriptMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /* 遮罩期间队列被压住不放，所以它绝不能卡死。任何原因（roster 拉不回来、
   * 某个 zip 404、waitForLoadIdle 超时）导致预载迟迟不完，都放行到"就绪待开场"，
   * 剩下的角色退回边演示边挂——慢总比开不了场好。
   *
   * 注意这里只解除预载遮罩，不代表开演：还要演示者点「开始」。
   * 所以刻意不提供手动"跳过"按钮——没有哪个操作能让台上提前开口。 */
  const skipPreload = useCallback((reason) => {
    preloadSkipRef.current = true;
    setPreloadPhase((prev) => (prev === "done" ? prev : "skipped"));
    debugLog.emit("RENDER", `⏭️ 跳过启动预载（${reason}），剩下的角色边演示边挂`);
  }, []);
  // 按"多久没有进展"计时而不是总耗时：角色多、机器慢时总耗时可以很长，
  // 但只要还在一个个往前推就不该打断；真卡死了 45 秒也足够识别。
  useEffect(() => {
    if (preloadPhase === "done" || preloadPhase === "skipped") return undefined;
    const t = setTimeout(() => skipPreload("45s 无进展"), 45000);
    return () => clearTimeout(t);
  }, [preloadPhase, preloadDone, skipPreload]);

  /* 预载遮罩期间压住队列：主持人的开场白会照常从 WebSocket 到达并入队，
   * 但不能让它在遮罩后面自己说完——观众什么都没看到。 */
  const showHeldByPreload = preloadPhase === "pending" || preloadPhase === "running";
  /* 资源就绪但还没点「开始」：继续沿用上面那套"压住队列"的机制，
   * 只是把放行条件从"预载完成"改成"预载完成且演示者点了开始"。
   *
   * 录课模式由脚本自动推进，没有演示者在场按按钮，必须维持原行为。
   *
   * ⚠️ 闸门期间绝不能调 speak()：一旦发出去，waitForPlaybackStart(15s)、
   * waitForPlaybackEnd(20–120s) 就开始计时，演示者慢一点超时就会误判"播完"，
   * drainQueue 走到 advanceQueue 把开场白弹出队列 → 点「开始」时没话可说。
   * 不下发文本就没有任何计时，WS 那边有 30s 心跳保活，
   * 万一 session 真过期 speak 的 404 分支也会自动重连重试，放多久都能点。
   *
   * waitingForStart 在上面 stageBusy 处已声明（TDZ 所致）。 */
  const holdStage = showHeldByPreload || waitingForStart;

  const latestSummary = useMemo(() => findLatestPhaseSummary(messages), [messages]);
  const parsedSummary = useMemo(() => parseExpoPhaseSummary(latestSummary), [latestSummary]);

  /* ──── 右栏对话流：头像 URL 表 ────
   * 启动期把 virtualRoster 每个 agent 的 PNG URL 异步加载进 state（命中模块级缓存，零额外 HTTP）。
   * 渲染对话条时直接 O(1) 查表，不再为每条消息单独 fetch config。 */
  const [agentPreviewMap, setAgentPreviewMap] = useState({}); // {agentName: pngUrl}
  useEffect(() => {
    if (!virtualRoster || virtualRoster.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const next = {};
      for (const agent of virtualRoster) {
        if (cancelled) return;
        // 主持人也参与查表（同样能取到 avatarPath，左侧候场区已经是这套数据）。
        const name = getAgentName(agent);
        if (!name) continue;
        try {
          const cfg = await fetchDigitalHumanConfigCached(name, projectWorkflowName, "science_expo");
          if (!cfg?.avatarPath) continue;
          const previewPath = String(cfg.avatarPath).replace(/\.zip(\?.*)?$/i, ".png$1");
          next[name] = previewPath.startsWith("http") ? previewPath : `${DIGITAL_HUMAN_URL}${previewPath}`;
        } catch (_) {}
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setAgentPreviewMap((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => { cancelled = true; };
  }, [virtualRoster, projectWorkflowName]);

  /* ──── 右栏对话流：构建 feed 数据 ────
   * 从 messages 抽取所有可展示条目，按时间序列。
   * 三种类型：
   *   - speech     : host / agent / student / member 发言（取 voice 字段）
   *   - summary    : 阶段总结（取 SUMMARY display 段）
   *   - system     : 系统提示
   * 对超长会话做窗口截断（最近 N 条），早期消息计数显示"已隐藏 X 条"。 */
  const FEED_WINDOW = 80;
  const feedItems = useMemo(() => {
    const items = [];
    for (const m of messages || []) {
      const ts = m?.timestamp ? new Date(m.timestamp) : null;
      if (m?.type === "host_read_receipt") {
        items.push({
          key: `receipt-${m.messageId || m.id || ts?.getTime() || items.length}`,
          kind: "read_receipt",
          label: m.content || "已收到发言",
          detail: m.detail || "想让专家回应，可以直接提问或点名某位专家。",
          timestamp: ts,
        });
        continue;
      }
      if (isPhaseSummaryMessage(m)) {
        const parsed = parseExpoPhaseSummary(String(m.content || ""));
        if (!parsed.display) continue;
        items.push({
          key: `sum-${m.messageId || m.id || ts?.getTime() || items.length}`,
          kind: "summary",
          title: m.phaseLabel ? `阶段小结 · ${m.phaseLabel}` : "阶段小结",
          markdown: parsed.display,   // 卡片内展示（摘要段）
          full: parsed.full || parsed.display,  // 弹窗展示（完整报告）
          timestamp: ts,
        });
        continue;
      }
      const senderType = m.senderType;
      if (senderType === "system") {
        const text = getMessageText(m);
        if (!text) continue;
        items.push({
          key: `sys-${m.messageId || m.id || ts?.getTime() || items.length}`,
          kind: "system",
          text,
          timestamp: ts,
        });
        continue;
      }
      if (senderType !== "host" && senderType !== "agent" && senderType !== "student" && senderType !== "member") continue;
      const parsed = parseExpoVoiceDetail(m.content || "");
      const voice = parsed.voice || getMessageText(m);
      if (!voice) continue;
      // detail 优先取 <detail> 标签内的完整文本；如果没有 hasDetail，但 voice 与
      // detail（去标签后的整段）不一致（例如全文被截短），仍允许展开看完整 message。
      const fullDetail = parsed.detail && parsed.detail !== voice ? parsed.detail : "";
      items.push({
        key: `msg-${m.messageId || m.id || `${m.senderId}-${ts?.getTime() || items.length}`}`,
        kind: "speech",
        senderType,
        senderId: m.senderId,
        senderName: senderType === "host" ? (hostDisplayName || m.senderName || "主持人") : (m.senderName || m.senderId || ""),
        senderRole: m.senderRole || "",
        text: voice,
        detail: fullDetail,
        hasDetail: parsed.hasDetail || !!fullDetail,
        timestamp: ts,
      });
    }
    return items;
  }, [messages, hostDisplayName]);
  const feedHidden = Math.max(0, feedItems.length - FEED_WINDOW);
  const feedVisible = feedHidden > 0 ? feedItems.slice(-FEED_WINDOW) : feedItems;

  /* ──── 右栏对话流：自动滚到底 ────
   * 贴底跟滚动容器真实高度走（ResizeObserver）。「翻历史」只认对话流上的真实手势，
   * 程序滚动不会上锁。科技馆对话流无触摸时手势永不发生，因此恒定贴底。
   * 「最新」仍保留：窗口开发用滚轮上翻后可恢复；现场误判时也能自救。 */
  const feedScrollRef = useRef(null);
  const feedContentRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const feedGestureUntilRef = useRef(0);
  const feedAutoScrollRef = useRef(false);
  const [showJumpLatest, setShowJumpLatest] = useState(false);
  /* 综合模式全屏时，"最新"改放到麦克风按钮右侧（对话流右下角在落地大屏的死区里，
   * 观众和讲解员都看不到）。其余形态仍浮在对话流右下角。 */
  const jumpLatestInMicRow = layoutVariant === "comprehensive" && isFullscreen;
  const markFeedGesture = useCallback(() => {
    feedGestureUntilRef.current = performance.now() + 150;
  }, []);
  const handleFeedScroll = () => {
    if (feedAutoScrollRef.current) return;
    const el = feedScrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom <= 60) {
      userScrolledUpRef.current = false;
      setShowJumpLatest(false);
      return;
    }
    if (performance.now() <= feedGestureUntilRef.current) {
      userScrolledUpRef.current = true;
      setShowJumpLatest(true);
    }
  };
  const scrollFeedToBottom = useCallback((smooth = false) => {
    const el = feedScrollRef.current;
    if (!el) return;
    feedAutoScrollRef.current = true;
    /* instant：不受 CSS scroll-behavior 影响。auto 会沿用元素计算值，曾经把贴底变成平滑动画。 */
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "instant" });
    userScrolledUpRef.current = false;
    setShowJumpLatest(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { feedAutoScrollRef.current = false; });
    });
  }, []);
  useEffect(() => {
    if (feedItems.length === 0) {
      userScrolledUpRef.current = false;
      setShowJumpLatest(false);
    }
  }, [feedItems.length]);
  useEffect(() => {
    const el = feedScrollRef.current;
    const inner = feedContentRef.current;
    if (!el || !inner) return undefined;
    const pinIfFollowing = () => {
      if (userScrolledUpRef.current) return;
      scrollFeedToBottom(false);
    };
    pinIfFollowing();
    const ro = new ResizeObserver(pinIfFollowing);
    ro.observe(el);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [scrollFeedToBottom, feedItems.length, silentTurnNotice?.id]);

  /* 对话流详情：点击气泡弹出完整内容（不阻断舞台，遮罩 + ESC 关闭）。 */
  const [feedDetail, setFeedDetail] = useState(null); // {item} | null
  useEffect(() => {
    if (!feedDetail) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFeedDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [feedDetail]);

  // 兼容老代码（findPhaseIndex 之外仍有引用 summaryExpanded 的 hooks，保留无副作用占位）
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  useEffect(() => { setSummaryExpanded(false); }, [latestSummary]);
  useEffect(() => {
    if (!summaryExpanded || !summaryScrollRef.current) return undefined;
    const el = summaryScrollRef.current;
    el.scrollTop = 0;
    const timer = window.setInterval(() => {
      if (!el) return;
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 0) return;
      if (el.scrollTop >= max - 2) {
        el.scrollTop = 0;
      } else {
        el.scrollTop += 1;
      }
    }, 55);
    return () => window.clearInterval(timer);
  }, [summaryExpanded, latestSummary]);

  const activeSpeakerId   = expoState.activeSpeakerType === "member"
    ? expoState.activeSpeaker?.userId
    : expoState.activeSpeaker?.agentId || expoState.activeSpeaker?.id;
  const activeSpeakerName = expoState.activeSpeakerType === "member"
    ? getMemberName(expoState.activeSpeaker)
    : getAgentName(expoState.activeSpeaker);
  const activeSpeakerKey  = `${expoState.activeSpeakerType}:${activeSpeakerId || activeSpeakerName}`;
  const activeRoleRefKey  = expoState.activeSpeakerType === "member"
    ? `member:${activeSpeakerId || activeSpeakerName}`
    : expoState.activeSpeakerType === "agent"
      ? `agent:${activeSpeakerId || activeSpeakerName}`
      : "";

  useEffect(() => {
    setSpeechDetailOpen(false);
  }, [activeSpeakerKey, expoState.activeSpeechText]);

  const progressText = phases.length > 0
    ? `${Math.min(phaseIndex + 1, phases.length)} / ${phases.length}`
    : "准备中";

  /* ─── 发言队列管理 ─── */
  /* 入队：扫描 messages，把已 finalized 的 agent/host 消息按顺序加入队列。
   * 用 messageId 去重，保证同一条消息不会被加入两次。
   *
   * 多数消息在 </voice> 闭合时已经被下面那个 effect 提前入过队了，这里只负责
   * 给它补上 <detail>；只有早入队没覆盖到的（voice 太短、流没走 streamingContent）
   * 才真正在这里入队。 */
  useEffect(() => {
    const additions = [];
    const detailPatches = [];
    for (const m of messages || []) {
      if (isPhaseSummaryMessage(m)) continue;
      if (m.senderType !== "agent" && m.senderType !== "host") continue;
      const key = m.messageId || m.id || (m.seq != null
        ? `seq-${m.seq}`
        : `${m.senderId || ""}_${m.timestamp || ""}_${(m.content || "").slice(0, 24)}`);
      if (queuedKeysRef.current.has(key)) continue;
      const parsed = parseExpoVoiceDetail(m.content || "");
      if (!parsed.voice) continue; // 没有可读的 voice → 不入队（不会发声）
      queuedKeysRef.current.add(key);

      const early = earlyQueuedRef.current.get(m.senderId);
      if (early) {
        earlyQueuedRef.current.delete(m.senderId);
        if (early.voice !== parsed.voice) {
          // 保留早入队那条：它可能已经在播，中途换文本比念一段旧稿更糟
          debugLog.emit("QUEUE", `⚠️ 定稿 voice 与早入队不一致，沿用早入队：${m.senderName}`, "warn");
        }
        if (parsed.detail) detailPatches.push([early.key, parsed.detail]);
        continue;
      }
      const hostAgent = m.senderType === "host"
        ? { ...HOST_AGENT, displayName: hostDisplayName || m.senderName || "主持人" } : null;
      const agent = hostAgent
        || (agents || []).find((a) => a.agentId === m.senderId || a.id === m.senderId || getAgentName(a) === m.senderName)
        || { agentId: m.senderId, displayName: m.senderName };
      additions.push({
        key,
        senderId: m.senderId,
        senderName: m.senderName,
        senderType: m.senderType,
        agent,
        voice: parsed.voice,
        detail: parsed.detail,
        embodied: !!m.embodied,
      });
    }
    if (detailPatches.length > 0) {
      const byKey = new Map(detailPatches);
      setSpeechQueue((prev) => prev.map((it) => (
        byKey.has(it.key) ? { ...it, detail: byKey.get(it.key) } : it
      )));
    }
    if (additions.length > 0) {
      // 首条可播发言入队 = CC 群聊调度出结果，之后的延迟都归数字人链路
      latencyTrace.markTurn("queued", getAgentName(additions[0].agent));
      debugLog.emit("QUEUE", `➕ 入队 ${additions.length} 条：${additions.map((a) => getAgentName(a.agent)).join("/")}`);
      setSpeechQueue((prev) => [...prev, ...additions]);

      // P3 lazy mount：入队即请求 mount。后端 LLM 思考通常 5–15s，
      // 正好覆盖该角色 ExpertAvatar 的 mount + loadAvatar 时间，多数情况"入队 → 开口"零额外延迟。
      additions.forEach((add) => {
        const id = add.senderType === "host"
          ? "host"
          : (add.agent?.agentId || add.agent?.id || add.senderId || getAgentName(add.agent));
        requestMountAgent(id);
      });
    }
  }, [messages, agents, projectName, hostDisplayName]);

  /* 阶段小结到达时让主持人念出结尾那句问句。
   *
   * 小结走的是独立通道（isPhaseSummaryMessage 被上面的入队 effect 跳过），
   * 所以它不产生任何发言，也不会让 selfSpeechPhase 离开 waiting——现场表现就是
   * 字幕停在"已听到：…，主持人正在点人……"直到 30 秒兜底超时。
   * 这里补一条主持人发言，字幕、口型、TTS 全部复用既有通道。 */
  const spokenSummariesRef = useRef(new Set());
  const summarySeededRef = useRef(false);
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    // 刷新/重连时 messages 会一次性带回历史消息，里面的旧小结早就念过了，
    // 只登记不朗读，否则一进场就会听到上一个阶段的结语。
    if (!summarySeededRef.current) {
      summarySeededRef.current = true;
      for (const m of messages) {
        if (!isPhaseSummaryMessage(m)) continue;
        const raw = String(m.content || "").trim();
        if (!raw) continue;
        spokenSummariesRef.current.add(m.messageId || m.id || `sum-${raw.length}-${raw.slice(0, 24)}`);
      }
      return;
    }
    const summaryMsg = [...messages].reverse().find(isPhaseSummaryMessage);
    if (!summaryMsg) return;
    const raw = String(summaryMsg.content || "").trim();
    if (!raw) return;
    const key = summaryMsg.messageId || summaryMsg.id || `sum-${raw.length}-${raw.slice(0, 24)}`;
    if (spokenSummariesRef.current.has(key)) return;
    if (isRecordingScriptMode) return;
    spokenSummariesRef.current.add(key);

    const closing = extractSummaryClosing(raw);
    const agent = { ...HOST_AGENT, displayName: hostDisplayName || "主持人" };
    debugLog.emit("QUEUE", `➕ 阶段小结转主持人朗读：${closing}`);
    latencyTrace.markTurn("queued", getAgentName(agent));
    setSpeechQueue((prev) => [...prev, {
      key: `summary::${key}`,
      senderId: "host",
      senderName: getAgentName(agent),
      senderType: "host",
      agent,
      voice: closing,
      detail: raw,
    }]);
    requestMountAgent("host");

    // 小结不经过 deriveExpoState，activeSpeechText 不会变，waiting 得手工收掉
    setSelfSpeechPhase((prev) => (prev === "waiting" ? "idle" : prev));
    setAiTransitioning(true);
    if (waitTimerRef.current) { clearTimeout(waitTimerRef.current); waitTimerRef.current = null; }
  }, [messages, hostDisplayName, isRecordingScriptMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /* 流式剖面：只观测、不改行为。用来判断 CC 是边生成边推还是攒完整条才推。
   * 实测 <voice> 从第一个字到闭合只有 0.24–0.73s（3–7 次推送），
   * 所以句级切分没有意义，闭合已经是能拿到完整可朗读文本的最早时刻。 */
  const streamProfileRef = useRef(new Map());
  useEffect(() => {
    const entries = Object.entries(streamingContent || {})
      .filter(([agentId, data]) => !isPhaseSummaryStream(agentId, data));
    const alive = new Set(entries.map(([agentId]) => agentId));
    const now = performance.now();

    for (const [agentId, data] of entries) {
      let prof = streamProfileRef.current.get(agentId);
      if (!prof) {
        prof = { t0: now, name: data?.agentName || agentId, open: 0, first: 0, ticks: 0 };
        streamProfileRef.current.set(agentId, prof);
      }
      if (prof.done) continue;
      prof.ticks += 1;

      const content = String(data?.content || "");
      if (!prof.open && /<voice>/i.test(content)) prof.open = now;
      if (!prof.first) {
        // 只看 <voice> 开标签之后、闭标签之前的正文，避免把 detail 里的句号算进来
        const body = (content.split(/<voice>/i)[1] || "").split(/<\//)[0];
        if (/[。！？!?]/.test(body)) prof.first = now;
      }
      if (/<\/voice>/i.test(content)) {
        prof.done = true;
        const gap = (t) => (t ? `${((t - prof.t0) / 1000).toFixed(2)}s` : "—");
        debugLog.emit(
          "PRESPEAK",
          `📊 流式剖面 ${prof.name}：<voice> ${gap(prof.open)} · 首句 ${gap(prof.first)} · 闭合 ${gap(now)}（${prof.ticks} 次推送）`
        );
      }
    }

    for (const agentId of [...streamProfileRef.current.keys()]) {
      if (!alive.has(agentId)) streamProfileRef.current.delete(agentId);
    }
  }, [streamingContent]);

  /* ─── </voice> 闭合即入队 ─────────────────────────────────────────
   * 消息定稿（isEnd）比 </voice> 闭合晚 0.5–0.8s，那段时间 CC 还在写 <detail>，
   * 而 <detail> 不发声。既然可朗读的部分已经完整，就没必要等定稿再去发 TTS。
   *
   * 与定稿入队的衔接：早入队时把 senderId 登记进 earlyQueuedRef，
   * 定稿那一轮扫到同一 senderId 且 voice 一致，就只补 detail、不重复入队。
   *
   * 安全边界：
   *   - 只在 <voice> 已闭合且正文够长时才早入队，避免半截话或空壳被念出来
   *   - 后端只在 voice 为空/过弱时才用 detail 兜底改写，所以"定稿 voice 与早入队不一致"
   *     基本不可能。真发生时保留已入队那条（可能正在播），只记警告
   *   - 用户打断会清空队列但不清 earlyQueuedRef，所以随后到达的定稿消息会被识别为
   *     "已作废的那条"而不再入队，被打断的发言不会借尸还魂
   * ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    const entries = Object.entries(streamingContent || {})
      .filter(([agentId, data]) => !isPhaseSummaryStream(agentId, data));
    if (entries.length === 0) return;

    const additions = [];
    for (const [agentId, data] of entries) {
      if (earlyQueuedRef.current.has(agentId)) continue;
      const voice = data?.embodied
        ? extractEmbodiedVoice(data?.content)
        : extractClosedVoice(data?.content);
      if (voice.length < MIN_EARLY_ENQUEUE_CHARS) continue;

      const rosterAgent = (virtualRoster || []).find((a) => (a.agentId || a.id) === agentId);
      const isHost = !!rosterAgent?._isHost;
      const agent = isHost
        ? { ...HOST_AGENT, displayName: hostDisplayName || data?.agentName || "主持人" }
        : (rosterAgent
          || (agents || []).find((a) => a.agentId === agentId || a.id === agentId)
          || { agentId, displayName: data?.agentName });

      const key = `early::${agentId}::${voice.length}::${voice.slice(0, 16)}`;
      earlyQueuedRef.current.set(agentId, { key, voice });
      additions.push({
        key,
        senderId: agentId,
        senderName: data?.agentName || getAgentName(agent),
        senderType: isHost ? "host" : "agent",
        agent,
        voice,
        detail: "", // <detail> 还没流完，定稿那一轮补
        embodied: !!data?.embodied,
      });
    }

    if (additions.length === 0) return;
    latencyTrace.markTurn("queued", getAgentName(additions[0].agent));
    debugLog.emit(
      "QUEUE",
      `➕ 闭合即入队 ${additions.length} 条：${additions.map((a) => `${getAgentName(a.agent)}(${a.voice.length}字)`).join("/")}`
    );
    setSpeechQueue((prev) => [...prev, ...additions]);
    additions.forEach((add) => {
      requestMountAgent(add.senderType === "host" ? "host" : add.senderId);
    });
  }, [streamingContent, virtualRoster, agents, hostDisplayName]); // eslint-disable-line react-hooks/exhaustive-deps

  const queueHead = speechQueue[0] || null;

  /* 字幕跟"队列头"立即同步：advanceQueue 把上一位出队后，立刻把字幕切到下一位。
   *
   * 为何不等 onPlaybackStart？
   *   - audio 段先到、blendshapes 后到时，audio 会被暂存在 pendingAudioSegments，
   *     Player 直到 blendshapes 到达才创建 → onStartSpeaking 滞后真实"切角色"瞬间数秒
   *   - 用户感知就是"主持人字幕一直挂着，新角色都说一大半了字幕才切"
   *
   * advanceQueue 时上一位音频已完整播完（onEndSpeaking 触发的 advanceQueue），
   * 此时切字幕到下一位（虽然新角色音频还要再等几秒）远比"字幕滞后真实角色"自然。
   */
  useEffect(() => {
    if (!queueHead) return;
    const head = queueHead;
    if (head.senderType === "member") return; // member 走另一条同步路径
    const id = head.agent?.agentId || head.agent?.id || head.senderId || getAgentName(head.agent);
    const ds = {
      roleKey: `agent:${id}`,
      type: "agent",
      text: head.voice,
      detail: head.detail || "",
    };
    setDisplaySpeaker(ds);
    setCenterLock({ type: "agent", speaker: head.agent, text: head.voice });
    clearSilentGuide();
    setAiTransitioning(false);
  }, [queueHead, clearSilentGuide]);

  /* 每个 Agent 在队列尾部（不含 head）的待说数：用于左侧图标徽标 */
  const pendingCountByAgent = useMemo(() => {
    const map = new Map();
    for (let i = 1; i < speechQueue.length; i++) {
      const it = speechQueue[i];
      const id = it.senderId || (it.agent && (it.agent.agentId || it.agent.id)) || it.senderName || "";
      if (!id) continue;
      map.set(id, (map.get(id) || 0) + 1);
    }
    return map;
  }, [speechQueue]);

  /* 弹出队首：在数字人 onPlaybackEnd 时调用
   * ⚠️ React 18 StrictMode（开发模式）会双调 reducer 以校验纯函数性，
   *    日志类副作用必须放在 reducer 之外，否则会出现"出队两次"假象。
   *    真正的去重靠 reducer 内部的 length 检查 + queueRunningRef，已经足够。 */
  const advanceQueue = useCallback(() => {
    let popped = null;
    let remaining = 0;
    setSpeechQueue((prev) => {
      if (prev.length === 0) return prev;
      const [done, ...rest] = prev;
      popped = done;
      remaining = rest.length;
      return rest;
    });
    if (popped) {
      debugLog.emit("QUEUE", `✔️ 出队：${getAgentName(popped.agent)}（剩余 ${remaining}）`);
    }
  }, []);

  /* pendingAgentRoleKey：兼容旧字段（其他 effect 仍在用），但语义改为"队列头"对应 key */
  const pendingAgentRoleKey = queueHead
    ? `agent:${queueHead.agent.agentId || queueHead.agent.id || queueHead.senderId || getAgentName(queueHead.agent)}`
    : "";

  /* ─── 等待期的占位反馈 ──────────────────────────────────────────
   * 观众松手后到数字人开口之间有好几秒完全静默，台上没有任何动静，
   * 现场观感上这段比它的实际长度更难熬。
   *
   * 这里不缩短延迟，只把这段时间里后台真实发生的事讲出来：
   *   松手 → 已听到你的问题 → 主持人在点人 → 某某正在组织语言 → 开口
   * 中间那一跳有真实信号：streamingContent 里出现某个角色，说明 CC 已经点到他、
   * 正在生成他的发言。这比"AI 正在思考"早好几秒，而且能告诉观众该看谁。
   * ──────────────────────────────────────────────────────────── */
  const thinkingAgent = useMemo(() => {
    const entry = Object.entries(streamingContent || {})
      .find(([agentId, data]) => !isPhaseSummaryStream(agentId, data));
    if (!entry) return null;
    const [agentId, data] = entry;
    const rosterAgent = (virtualRoster || []).find((a) => (a.agentId || a.id) === agentId);
    return {
      agentId,
      name: (rosterAgent ? getAgentName(rosterAgent) : "") || data?.agentName || "",
    };
  }, [streamingContent, virtualRoster]);

  /* 逐字浮现：等待期的提示语一个字一个字出，让静默期始终有东西在动。
   * 文案变了就从头再打一遍。 */
  const [typedLen, setTypedLen] = useState(0);
  const waitingHint = selfSpeechPhase === "waiting"
    ? (queueStatusText
      ? queueStatusText
      : (thinkingAgent?.name
        ? `${thinkingAgent.name} 正在组织语言……`
        : (voiceTranscript ? `已听到："${voiceTranscript}"，主持人正在点人……` : "主持人正在点人……")))
    : "";
  useEffect(() => {
    setTypedLen(0);
    if (!waitingHint) return undefined;
    const timer = setInterval(() => {
      setTypedLen((n) => {
        if (n >= waitingHint.length) { clearInterval(timer); return n; }
        return n + 1;
      });
    }, 45);
    return () => clearInterval(timer);
  }, [waitingHint]);

  /* 他人此刻正按住麦克风（来自 member_speaking 实时信号）。
   * centerMemberEntry 要等 ASR 出结果、消息回流才有值，中间那几秒中央是上一位的形象；
   * 这条信号把"谁在说"提前到按下的瞬间。AI 已接过话筒时不抢中央，与 selfSpeakingEntry 同规则。 */
  const liveSpeakingMemberEntry = useMemo(() => {
    if (queueHead) return null;
    if (!speakingMemberIds || speakingMemberIds.size === 0) return null;
    return members.find(
      (member) => member?.userId && member.userId !== myUserId && speakingMemberIds.has(member.userId)
    ) || null;
  }, [queueHead, speakingMemberIds, members, myUserId]);

  /* 他人正按住麦克风、但这一轮还没出文字时，displaySpeaker 仍停在上一位 AI 的正文上。
   * 中央头像和名字此刻已经是这位同学，字幕再挂着旧正文就成了错配（观众会以为这位同学
   * 说了 AI 那段话）。等这位同学的消息回流、displaySpeaker 切到他本人时自然恢复。 */
  const liveHumanRoleKey = liveSpeakingMemberEntry
    ? `member:${liveSpeakingMemberEntry.userId || getMemberName(liveSpeakingMemberEntry)}`
    : "";
  const staleSubtitleForLiveHuman = !!liveHumanRoleKey && displaySpeaker?.roleKey !== liveHumanRoleKey;

  /* 字幕文本逻辑：
   * - 自己 recording → "🎤 正在聆听  …" + 实时 voiceTranscript
   * - 自己 recognizing → "⏳ 识别中…"
   * - 自己 waiting → "⏳ 等待回应…"
   * - 其他时候 → 当前角色字幕
   */
  let subtitleText = "";
  let subtitlePrefix = "";
  if (selfSpeechPhase === "arming") {
    // 麦克风连接中。预滚动缓冲会保住这段时间的语音，所以用户可以直接开口。
    subtitlePrefix = "🎤 ";
    subtitleText = "正在聆听（请说话）";
  } else if (selfSpeechPhase === "recording") {
    subtitlePrefix = "🎤 ";
    subtitleText = voiceTranscript || "正在聆听（请说话）";
  } else if (selfSpeechPhase === "recognizing") {
    subtitlePrefix = "⏳ ";
    subtitleText = voiceTranscript ? `"${voiceTranscript}"` : "识别中…";
  } else if (selfSpeechPhase === "waiting") {
    // 收到 AI 回应之前：先确认听到了你说的话，再报出正在组织语言的角色
    subtitlePrefix = thinkingAgent?.name ? "🎙️ " : "💭 ";
    subtitleText = waitingHint.slice(0, typedLen);
  } else if (aiTransitioning) {
    // 收到回应文本但数字人还没开口的过渡期：避免闪现上一位 AI 的旧字幕
    subtitlePrefix = "🎙️ ";
    subtitleText = "AI 正在准备发言……";
  } else if (silentGuide) {
    subtitlePrefix = silentGuide.kind === "low_information" ? "提示：" : "已收到：";
    subtitleText = [silentGuide.label, silentGuide.text].filter(Boolean).join("。");
  } else if (staleSubtitleForLiveHuman) {
    subtitleText = "";
  } else {
    subtitleText = displaySpeaker?.text || "";
  }
  const activeDetailText = displaySpeaker?.detail || "";
  const canExpandSpeechDetail = selfSpeechPhase === "idle" && !aiTransitioning && !silentGuide
    && !staleSubtitleForLiveHuman && !!activeDetailText;
  const isSelfSpeechHint = (
    selfSpeechPhase === "arming"
    || (selfSpeechPhase === "recording" && !voiceTranscript)
    || (selfSpeechPhase === "recognizing" && !voiceTranscript)
  );
  const isStatusHint = isSelfSpeechHint || !!silentGuide;

  /* centerAgentEntry：
   * - 队列头存在 → 直接用 queueHead.agent（含 host 完整信息，不再走 roster 反查）
   * - 否则用 centerLock 里的 agent（保留上一位发言者作为 fallback）
   */
  const centerAgentEntry = useMemo(() => {
    if (queueHead) {
      // 优先在 roster 里找完整信息（带 voice/avatarPath 配置），找不到就用 queueHead.agent
      const id = queueHead.agent.agentId || queueHead.agent.id || queueHead.senderId;
      const fromRoster = virtualRoster.find((a) => {
        const aid = a.agentId || a.id;
        return aid === id || getAgentName(a) === getAgentName(queueHead.agent);
      });
      return fromRoster || queueHead.agent;
    }
    if (centerLock?.type === "agent") return centerLock.speaker;
    return null;
  }, [queueHead, centerLock, virtualRoster]);

  /* 当前发言者是人类成员（非自己，且来自 centerLock） */
  const centerMemberEntry = useMemo(() => {
    if (pendingAgentRoleKey) return null;
    if (centerLock?.type !== "member") return null;
    return centerLock.speaker || null;
  }, [pendingAgentRoleKey, centerLock]);
  /* 自己发言时的中央实体。成员发言同步刻意跳过自己（不顶替中央数字人），
   * 所以 centerLock 会一直停在上一位 AI 身上——现场表现就是"我在说话，中央还是
   * 上一个讲话人的形象"。这里单独补上自己，不改 centerLock 的既有语义。
   * queueHead 一出现就让位给 AI：那才是真正接过话筒的人。 */
  const selfSpeakingEntry = useMemo(() => {
    if (queueHead || !myUserId) return null;
    if (selfSpeechPhase === "idle") return null;
    return members.find((member) => member?.userId === myUserId) || null;
  }, [queueHead, myUserId, selfSpeechPhase, members]);

  /* 中央该显示的人类。自己按住麦克风时优先显示自己（这台屏幕的操作者就是话筒持有者），
   * 其次是正在说话的他人，最后才回落到"最后一位发过言的成员"。 */
  const centerHumanEntry = selfSpeakingEntry || liveSpeakingMemberEntry || centerMemberEntry;

  useEffect(() => {
    const live = speakingMemberIds && speakingMemberIds.size > 0
      ? [...speakingMemberIds].join(",")
      : "空";
    debugLog.emit("SPEAKER", `📡 正在说话=[${live}]｜成员=[${members.map((m) => m?.userId).join(",")}]`);
  }, [speakingMemberIds, members]);
  /* 头像键有两种可能：deriveExpoState 找不到成员时会用 senderId 造兜底对象，
   * 那个 userId 未必命中 memberAvatarMap（键按 members 列表构建）。两种键都试一遍，
   * 都查不到才落到文字徽章。 */
  const centerHumanAvatarUrl = centerHumanEntry
    ? (memberAvatarMap.get(`member:${centerHumanEntry.userId || getMemberName(centerHumanEntry)}`)
      || memberAvatarMap.get(`member:${getMemberName(centerHumanEntry)}`)
      || "")
    : "";

  /* ─── 成员发言同步（非自己才更新中央） ─── */
  useEffect(() => {
    if (expoState.activeSpeakerType !== "member" || !expoState.activeSpeechText || !activeRoleRefKey) return;
    const speakerId = expoState.activeSpeaker?.userId;
    if (speakerId && speakerId === myUserId) return; // 自己说话：不顶替中央
    setVoiceTranscript("");
    const ds = { roleKey: activeRoleRefKey, type: "member", text: expoState.activeSpeechText, detail: "" };
    setDisplaySpeaker(ds);
    setCenterLock({ type: "member", speaker: expoState.activeSpeaker, text: expoState.activeSpeechText });
    debugLog.emit("SPEAKER", `🗣️ 成员发言：${getMemberName(expoState.activeSpeaker)}`);
  }, [activeRoleRefKey, activeSpeakerKey, expoState.activeSpeechText, expoState.activeSpeakerType, expoState.activeSpeaker, myUserId]);

  /* 成员条高亮：他人发言时点亮，到点自动熄灭。只写 recentMemberKey，
   * 不碰 centerLock / displaySpeaker，中央舞台的行为完全不变。 */
  useEffect(() => {
    if (expoState.activeSpeakerType !== "member" || !expoState.activeSpeechText || !activeRoleRefKey) return;
    const speakerId = expoState.activeSpeaker?.userId;
    if (speakerId && speakerId === myUserId) return; // 自己那枚走 selfSpeechPhase
    setRecentMemberKey(activeRoleRefKey);
    if (recentMemberTimerRef.current) clearTimeout(recentMemberTimerRef.current);
    recentMemberTimerRef.current = setTimeout(() => {
      recentMemberTimerRef.current = null;
      setRecentMemberKey("");
    }, MEMBER_SPEAKING_HIGHLIGHT_MS);
  }, [activeRoleRefKey, activeSpeakerKey, expoState.activeSpeechText, expoState.activeSpeakerType, expoState.activeSpeaker, myUserId]);

  /* 有虚拟角色接过话筒 → 立刻熄灭，不等那 4 秒走完 */
  useEffect(() => {
    if (!pendingAgentRoleKey) return;
    if (recentMemberTimerRef.current) {
      clearTimeout(recentMemberTimerRef.current);
      recentMemberTimerRef.current = null;
    }
    setRecentMemberKey("");
  }, [pendingAgentRoleKey]);

  /* waiting → AI 准备中：当出现 agent / host 类型的新发言才算"收到 AI 回应"。
   * member 类型（含自己消息回流、其他同学）不计入 → 避免 effect 立即误判。 */
  useEffect(() => {
    if (selfSpeechPhase !== "waiting") return;
    // 1. 排除人类消息（自己消息回流、其他同学发言）
    if (expoState.activeSpeakerType === "member") return;
    // 2. 排除空文本
    if (!expoState.activeSpeechText) return;
    // 3. 排除"还是 waiting 之前那条 AI 文本"
    if (expoState.activeSpeechText === waitBaselineRef.current) return;
    // 4. 排除"恰好等于刚识别的语音文本"（双保险）
    if (voiceTranscript && expoState.activeSpeechText === voiceTranscript) return;
    // ✅ 真的来了 AI 回应：进入"过渡态"，等数字人 onPlaybackStart 再切正文
    clearSilentGuide();
    setSelfSpeechPhase("idle");
    setAiTransitioning(true);
    if (waitTimerRef.current) { clearTimeout(waitTimerRef.current); waitTimerRef.current = null; }
    debugLog.emit("STATE", "✅ 收到 AI 回应（过渡中），等待数字人开口");
  }, [selfSpeechPhase, expoState.activeSpeechText, expoState.activeSpeakerType, voiceTranscript, clearSilentGuide]);

  /* 主持人明确选择静默时，本轮已经结束：立即解除 waiting，并在学生视线焦点
   * （字幕区）给出可执行的下一步。7 秒后字幕恢复，麦克风上方仍保留持久提示。 */
  useEffect(() => {
    if (!silentTurnNotice) {
      clearSilentGuide();
      return;
    }
    if (waitTimerRef.current) {
      clearTimeout(waitTimerRef.current);
      waitTimerRef.current = null;
    }
    setSelfSpeechPhase((prev) => (prev === "waiting" ? "idle" : prev));
    setAiTransitioning(false);
    setSilentGuide(silentTurnNotice);
    if (silentGuideTimerRef.current) clearTimeout(silentGuideTimerRef.current);
    silentGuideTimerRef.current = setTimeout(() => {
      silentGuideTimerRef.current = null;
      setSilentGuide(null);
    }, 7000);
  }, [silentTurnNotice, clearSilentGuide]);

  /* 上报"我正按住麦克风"，让其他客户端立刻点亮我这枚头像，不必等 ASR 出结果。
   * 用 ref 读最新回调，避免回调变化把 ASR 实例的 effect 重建掉（那会重连麦克风）；
   * 同时做去重，只在状态真正翻转时发包。 */
  const speakingReportRef = useRef(false);
  const onSpeakingStateChangeRef = useRef(onSpeakingStateChange);
  onSpeakingStateChangeRef.current = onSpeakingStateChange;
  const reportSpeaking = useCallback((speaking) => {
    if (speakingReportRef.current === speaking) return;
    speakingReportRef.current = speaking;
    try { onSpeakingStateChangeRef.current?.(speaking); } catch (_) {}
  }, []);

  /* ─── 语音识别 ─── */
  const sendVoiceSpeech = useCallback((text) => {
    const content = String(text || "").trim();
    if (!content || content === lastVoiceSentRef.current || !onSendMessage) return;
    // 训练结束后禁止再向已停止的 CC 引擎发消息
    if (status === "completed") return;
    lastVoiceSentRef.current = content;
    latencyTrace.markTurn("send");
    onSendMessage({ content, mentions: [] });
  }, [onSendMessage, status]);

  useEffect(() => {
    // P0：常驻麦克风/AudioContext + 短停止超时（按住说话场景）。
    // keepMicWarm 让按下后只需连 WebSocket，onstart 近乎瞬时，不再吞前段语音；
    // stopTimeoutMs 调小，避免"识别中"长时间卡住。
    const rec = new FunASRClient({ keepMicWarm: true, stopTimeoutMs: 2500 });
    rec.onstart  = () => {
      recordingStartedRef.current = true;
      // 以下两个提前返回分支对应"用户已经松手"：UI 已由 stopVoice 置好，
      // 这里若再翻成 recording 会出现按钮跳变。
      if (cancelOnStartRef.current) {
        cancelOnStartRef.current = false;
        debugLog.emit("ASR", "🛑 onstart 后立即 abort（判为误触）");
        // abort() 不触发 onend，需手动复位，否则下次 startVoice 被拦截
        try { recognitionRef.current?.abort(); } catch (_) {}
        recordingStartedRef.current = false;
        return;
      }
      // 用户在握手完成前就说完松手了：预滚动音频已随 start() 补发，直接收口送识别。
      // UI 已由 stopVoice 置为 recognizing，这里不要再翻回 recording。
      if (pendingStopRef.current) {
        pendingStopRef.current = false;
        debugLog.emit("ASR", "⏹️ onstart 后立即停止（用户已说完松开，预滚动音频已补发）");
        try { recognitionRef.current?.stop(); } catch (_) {}
        return;
      }
      setIsVoiceRecording(true);  setIsVoiceProcessing(false);
      setVoiceTranscript(""); setVoiceError(""); lastVoiceSentRef.current = "";
      // 仅当 phase 还在 arming 时才转 recording；若用户已松开则 stopVoice 已置 idle，不要回头覆盖
      setSelfSpeechPhase((p) => (p === "arming" ? "recording" : p));
      debugLog.emit("ASR", "🎤 开始录音（可以说话了）");
    };
    rec.onend    = () => {
      recordingStartedRef.current = false;
      reportSpeaking(false);
      setIsVoiceRecording(false); setIsVoiceProcessing(false);
      // 兜底：若 onresult 始终没触发（如几乎无音频），phase 可能卡在 recording/recognizing
      setSelfSpeechPhase((p) => (p === "recognizing" || p === "recording" || p === "arming") ? "idle" : p);
      debugLog.emit("ASR", "🎤 录音结束");
    };
    rec.onresult = (e)  => {
      const t = e.results?.[0]?.[0]?.transcript || "";
      setIsVoiceProcessing(false);
      // 误触：太短按下不发送，也不切到 waiting
      if (tooShortRef.current) {
        tooShortRef.current = false;
        setSelfSpeechPhase("idle");
        return;
      }
      setVoiceTranscript(t);
      latencyTrace.markTurn("asr", `${t.length}字`);
      debugLog.emit("ASR", `📝 识别：${t}`);
      if (t) {
        sendVoiceSpeech(t);
        // 记录进入 waiting 那一刻的"旧 text 基线"，effect 比较时跳过它
        waitBaselineRef.current = expoStateRef.current?.activeSpeechText || "";
        setSelfSpeechPhase("waiting");
        // 30 秒兜底超时
        if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
        waitTimerRef.current = setTimeout(() => {
          setSelfSpeechPhase("idle");
          waitTimerRef.current = null;
          latencyTrace.discard("turn", "30s 未等到回应");
          debugLog.emit("STATE", "⏰ 等待回应超时，回到 idle", "warn");
        }, 30000);
      } else {
        setSelfSpeechPhase("idle");
      }
    };
    rec.onerror  = (e)  => {
      console.error("[ScienceExpoStage] ASR error:", e.error);
      reportSpeaking(false);
      setIsVoiceRecording(false); setIsVoiceProcessing(false);
      // start() 启动阶段超时后必须一并作废这次按下记录：
      // 用户随后松开空格仍会触发 keyup；若残留 pressDownTs，就会被 stopVoice
      // 误当作一轮有效发言，重新写入 pendingStop，把刚失败的旧会话再拉回来。
      pressDownTsRef.current = 0;
      cancelOnStartRef.current = false;
      pendingStopRef.current = false;
      const detail = String(e?.error || "");
      setVoiceError(
        detail.includes("超时")
          ? "语音启动超时，请松开后重新按住说话。"
          : "语音识别暂不可用，请检查麦克风权限。"
      );
      setSelfSpeechPhase("idle");
      debugLog.emit("ASR", `❌ ${e.error}`, "error");
    };
    recognitionRef.current = rec;
    // P0：进入展示模式即预热麦克风/采集管线（首次会弹一次权限申请，之后按下即可说）。
    try { rec.prewarm?.(); } catch (_) {}
    return () => {
      try { recognitionRef.current?.dispose?.(); } catch (_) {}
      recognitionRef.current = null;
    };
  }, [sendVoiceSpeech, reportSpeaking]);

  // 按下时机戳：用于判断"过短按下"（< 500ms 视为误触/抖动，松开时不发送）。
  // 按下瞬间起的音频由 FunASRClient 的预滚动缓冲保住，所以按住时长是唯一的有效性判据，
  // 不能再用"onstart 到松手"来衡量——那个值只反映 WebSocket 握手快慢。
  const PRESS_MIN_MS = 500;
  const pressDownTsRef = useRef(0);
  const tooShortRef = useRef(false);
  // ASR 是否已真正 onstart（用于 stopVoice 判断走哪条路径）
  const recordingStartedRef = useRef(false);
  // 用户在 onstart 之前就松开了（按住过短，判为误触）→ onstart 触发后立即 abort
  const cancelOnStartRef = useRef(false);
  // 用户有效按住后、但在 onstart 之前就松开了 → onstart 触发后立即 stop 并送识别。
  // 预滚动缓冲已经留住了整段语音，不能像误触那样丢弃。
  const pendingStopRef = useRef(false);
  // 空格键是否处于按下状态：过滤 key repeat，避免重复 startVoice
  const spaceHoldingRef = useRef(false);
  // 空格心跳监护：keyup 可能因焦点转移（数字人 iframe 抢焦点、系统弹窗、切窗口）而丢失，
  // 表现为松开后按钮永远卡在按住状态。按住期间 keydown repeat 会持续到达，
  // 超过窗口没有任何 Space keydown 即视为已松开，自动收口。
  const lastSpaceDownTsRef = useRef(0);
  const spaceHeartbeatRef = useRef(null);
  // 本次按住是否真的收到过 keydown repeat。看门狗以"repeat 断流"为松手判据，
  // 只有先证实这台机器/这次按住确实在发 repeat，该判据才成立；否则必须彻底闭嘴，
  // 退回纯 keyup 收口。曾因无条件启用而在 1.2s 处把还按着的键误判为松开：
  // 那一刻 heldMs≈1200 > PRESS_MIN_MS，于是把开口前的静音当作发言送去识别，
  // 返回空串 → 不出字不发消息；后续 repeat 又都被 e.repeat 分支挡掉无法恢复，
  // 最终松手时 spaceHolding 已为 false，keyup 也不做任何事 —— 表现为"按了完全没反应"。
  const sawSpaceRepeatRef = useRef(false);
  // 绝对上限：无论 repeat 有无，按住超过它一定收口，防止 keyup 真丢时永久卡住。
  const spaceHoldStartTsRef = useRef(0);
  // 清空 AI 待播队列的延时器：按住超过 PRESS_MIN_MS 才算真发言，届时才清队列。
  // 立即清会让一次误触把 AI 排队要说的话一并清掉，现场表现为"AI 无响应"。
  const queueClearTimerRef = useRef(null);

  const startVoice = useCallback(async (e) => {
    e.preventDefault(); e.stopPropagation();
    // 🔊 M4：在用户手势栈内 resume 共享 AudioContext，让首段音频不被自动播放策略拦截
    try { ensureSharedAudioContext(); } catch (_) {}
    // 注意：用 ref 判断而非 state 闭包，避免 stale closure
    if (!recognitionRef.current) return;
    if (recordingStartedRef.current) return; // ASR 已在录音
    if (isVoiceProcessing) return;             // 上一次还在识别中

    // ① 立即视觉反馈：按钮即刻变色变文字。
    // arming 表示"已按下、WebSocket 还在连"。用户此刻就可以开口：
    // 握手期间的音频由 FunASRClient 的预滚动缓冲接住，连上后补发。
    setIsVoiceRecording(true);
    setVoiceError("");
    setVoiceTranscript("");
    setSelfSpeechPhase("arming");
    // 按下即广播，不等 onstart：其他客户端的高亮要跟按钮同时亮
    reportSpeaking(true);
    pressDownTsRef.current = Date.now();
    tooShortRef.current = false;
    cancelOnStartRef.current = false;
    pendingStopRef.current = false;
    // 新一轮提问：上一轮若没走完（被打断/超时）直接作废，不要把等待时间算进这一轮
    latencyTrace.beginTurn();
    latencyTrace.markTurn("press");

    // ② 立即打断所有数字人当前发言（用户要开口了，不能和 AI 抢话）
    // P3 多 spotlight 实例并存，遍历每一个都 interrupt
    interruptAllSpotlights();
    // 清空待说队列推迟到确认不是误触之后，见 queueClearTimerRef 注释
    if (queueClearTimerRef.current) clearTimeout(queueClearTimerRef.current);
    queueClearTimerRef.current = setTimeout(() => {
      queueClearTimerRef.current = null;
      // 不要在 React 的函数式 state updater 内 emit：开发模式下 updater 可能在 render
      // 期间执行，DebugConsole 订阅者 setState 会触发“rendering a different component”警告。
      debugLog.emit("QUEUE", "🧹 用户打断，清空待说队列", "warn");
      setSpeechQueue([]);
    }, PRESS_MIN_MS);

    // ③ 异步启动麦克风/ASR；失败则回退视觉
    try {
      await recognitionRef.current.start();
    } catch (err) {
      recordingStartedRef.current = false;
      cancelOnStartRef.current = false;
      pendingStopRef.current = false;
      reportSpeaking(false);
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
      setSelfSpeechPhase("idle");
      setVoiceError("无法启动麦克风，请检查浏览器权限。");
    }
  }, [isVoiceProcessing, interruptAllSpotlights, reportSpeaking]);

  const stopVoice = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    if (!recognitionRef.current) return;

    // 关键：把 pressDownTs 取出并立即清零，避免 mouseup+mouseleave 双触发、
    // 或后续因为 startVoice 早退（recordingStartedRef=true 时）导致使用残留的旧时间戳
    // 算出"伪长按"，进而错误地把 UI 设为"识别中"。
    const pressTs = pressDownTsRef.current || 0;
    pressDownTsRef.current = 0;
    // 松手即结束"正在说话"，无论后面走误触、pendingStop 还是正常收口
    reportSpeaking(false);

    // 没有有效的"按下记录" → 这次 stopVoice 是个幽灵事件（双触发或越权触发），直接忽略
    if (pressTs === 0) {
      debugLog.emit("ASR", "👻 stopVoice 但无有效按下记录，忽略", "warn");
      return;
    }

    const heldMs = Date.now() - pressTs;

    // 误触保护：按下时长 < PRESS_MIN_MS 视为抖动/误按，丢弃这次录音
    if (heldMs < PRESS_MIN_MS) {
      debugLog.emit("ASR", `⏱️ 按住 ${heldMs}ms < ${PRESS_MIN_MS}ms，判为抖动`, "warn");
      latencyTrace.discard("turn", "按住过短");
      tooShortRef.current = true;
      // 误触不算发言，AI 待播队列保持原样
      if (queueClearTimerRef.current) {
        clearTimeout(queueClearTimerRef.current);
        queueClearTimerRef.current = null;
      }
      if (!recordingStartedRef.current) {
        cancelOnStartRef.current = true;
      } else {
        // abort() 立即中止并复位标志，避免旧会话拖到下次按下
        try { recognitionRef.current.abort(); } catch (_) {}
        recordingStartedRef.current = false;
      }
      setIsVoiceRecording(false);
      setIsVoiceProcessing(false);
      setSelfSpeechPhase("idle");
      setVoiceTranscript("");
      return;
    }

    // 用户在 ASR onstart 之前松开（WebSocket 握手慢时很常见，空格键尤甚）：
    // 预滚动缓冲已经留住了按下之后的整段语音，所以这一轮是有效的，不能丢弃。
    // 标记 pendingStop，等 onstart 一到就收口送识别。
    if (!recordingStartedRef.current) {
      debugLog.emit("ASR", `⏸️ 松开时 ASR 尚未 onstart，标记 pendingStop (heldMs=${heldMs})`);
      pendingStopRef.current = true;
      latencyTrace.markTurn("release");
      setIsVoiceRecording(false);
      setIsVoiceProcessing(true);
      setSelfSpeechPhase("recognizing");
      return;
    }

    // 正常路径：ASR 真在录音
    latencyTrace.markTurn("release");
    debugLog.emit("ASR", `⏹️ 停止录音 (heldMs=${heldMs})`);
    try { recognitionRef.current.stop(); } catch (_) {}
    setIsVoiceProcessing(true);
    setSelfSpeechPhase("recognizing");
  }, [reportSpeaking]);

  /* 空格键 = 按住说话。复用 startVoice / stopVoice，确保鼠标、触摸、键盘走同一套状态机。 */
  useEffect(() => {
    const eventLike = (nativeEvent) => ({
      preventDefault: () => nativeEvent.preventDefault(),
      stopPropagation: () => nativeEvent.stopPropagation(),
    });
    const isEditableTarget = (target) => {
      if (!target || target === document.body) return false;
      // 麦克风按钮自身放行：用鼠标点过一次后它会持有焦点，此时空格必须仍走这套全局逻辑，
      // 否则同一个按键会落到两套不同的实现上。
      if (target.closest?.("[data-mic-hold]")) return false;
      // 全屏按钮同样放行：点击后它会持有焦点，空格否则会激活按钮而不是按住说话
      if (target.closest?.(".expo-icon-btn")) return false;
      const tag = String(target.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" ||
        target.isContentEditable || !!target.closest?.("button,[role='button']");
    };

    // keyup 丢失监护窗口：Windows 键盘 repeat 延迟默认约 500ms、之后每几十 ms 一次。
    // 放宽到 2.5s 是因为按下瞬间要跑 interruptAllSpotlights + 数字人渲染，
    // 主线程被占住一两秒时 repeat 会整批迟到，1.2s 的窗口会把还按着的键误判成松开。
    const SPACE_HEARTBEAT_TIMEOUT_MS = 2500;
    // 绝对上限：repeat 完全不来时唯一的兜底，只防"keyup 真丢 → 按钮永久卡住"，
    // 不参与正常收口。取值远大于任何正常发言时长，避免截断长发言。
    const SPACE_HOLD_MAX_MS = 60000;
    const syntheticEvent = { preventDefault: () => {}, stopPropagation: () => {} };

    const stopSpaceHeartbeat = () => {
      if (spaceHeartbeatRef.current) {
        clearInterval(spaceHeartbeatRef.current);
        spaceHeartbeatRef.current = null;
      }
    };

    const releaseSpaceHold = (label, ev) => {
      stopSpaceHeartbeat();
      if (!spaceHoldingRef.current) return;
      spaceHoldingRef.current = false;
      sawSpaceRepeatRef.current = false;
      debugLog.emit("ASR", label);
      stopVoice(ev || syntheticEvent);
    };

    const armHeartbeat = () => {
      if (spaceHeartbeatRef.current) return;
      spaceHeartbeatRef.current = setInterval(() => {
        if (!spaceHoldingRef.current) {
          stopSpaceHeartbeat();
          return;
        }
        if (Date.now() - spaceHoldStartTsRef.current > SPACE_HOLD_MAX_MS) {
          releaseSpaceHold("⌨️ 空格按住超过上限，强制结束按住说话");
          return;
        }
        // repeat 断流判据只在"已证实这次按住确实在发 repeat"时才成立，
        // 否则一台不发 repeat 的机器会被它在开口前就掐断（见 sawSpaceRepeatRef 注释）。
        if (!sawSpaceRepeatRef.current) return;
        if (Date.now() - lastSpaceDownTsRef.current > SPACE_HEARTBEAT_TIMEOUT_MS) {
          releaseSpaceHold("⌨️ 空格 keyup 丢失（心跳超时），自动结束按住说话");
        }
      }, 300);
    };

    const onKeyDown = (e) => {
      if (e.code !== "Space" || e.ctrlKey || e.metaKey || e.altKey) return;
      // 已在按住中：无论此刻焦点落在哪个元素，repeat 都必须刷新心跳。
      // 按下后的重渲染可能把焦点挪到某个 button 上，若还先过 isEditableTarget，
      // 后续 repeat 会被提前 return，时间戳刷不到，看门狗就被饿死后误判松手。
      if (spaceHoldingRef.current) {
        e.preventDefault();
        e.stopPropagation();
        lastSpaceDownTsRef.current = Date.now();
        if (e.repeat) sawSpaceRepeatRef.current = true;
        // 按住期间 effect 若重建过监听（cleanup 清掉了心跳），这里负责把它拉起来。
        armHeartbeat();
        return;
      }
      if (isEditableTarget(e.target)) return;
      if (e.repeat) return; // 上一轮已收口后仍按着不放：等松手重按，不在半途重启
      // 按住空格会连续触发 keydown repeat；必须全部 preventDefault，
      // 否则浏览器会滚动页面/抢占焦点，ASR start 可能被拖到 keyup 之后才真正 onstart。
      e.preventDefault();
      e.stopPropagation();
      lastSpaceDownTsRef.current = Date.now();
      spaceHoldStartTsRef.current = Date.now();
      sawSpaceRepeatRef.current = false;
      spaceHoldingRef.current = true;
      armHeartbeat();
      debugLog.emit("ASR", "⌨️ 空格按下，开始按住说话");
      void startVoice(eventLike(e));
    };

    const onKeyUp = (e) => {
      if (e.code !== "Space") return;
      if (!isEditableTarget(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
      releaseSpaceHold("⌨️ 空格松开，结束按住说话", eventLike(e));
    };
    const onWindowBlur = () => {
      releaseSpaceHold("⌨️ 窗口失焦，结束按住说话");
    };
    // 切标签页/系统弹窗遮挡时 keyup 同样会丢，这是比 repeat 心跳可靠得多的松手信号
    const onVisibilityChange = () => {
      if (document.hidden) releaseSpaceHold("⌨️ 页面转入后台，结束按住说话");
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      stopSpaceHeartbeat();
    };
  }, [startVoice, stopVoice]);

  /* 卸载时清理等待 timer */
  useEffect(() => () => {
    if (waitTimerRef.current) { clearTimeout(waitTimerRef.current); waitTimerRef.current = null; }
    if (silentGuideTimerRef.current) { clearTimeout(silentGuideTimerRef.current); silentGuideTimerRef.current = null; }
    // 离开展示模式时把话筒状态收干净，否则别人屏幕上我这枚会一直亮到 TTL 过期
    if (speakingReportRef.current) {
      speakingReportRef.current = false;
      try { onSpeakingStateChangeRef.current?.(false); } catch (_) {}
    }
    if (queueClearTimerRef.current) { clearTimeout(queueClearTimerRef.current); queueClearTimerRef.current = null; }
    if (recentMemberTimerRef.current) { clearTimeout(recentMemberTimerRef.current); recentMemberTimerRef.current = null; }
  }, []);

  /* 只进入、不切换。进度卡「开始」复用这条路径，避免误调切换函数把已全屏退掉。 */
  const enterStageFullscreen = useCallback(() => {
    if (document.fullscreenElement) return;
    const el = stageRootRef.current;
    const p = el?.requestFullscreen?.();
    if (p?.then) {
      p.then(() => releaseFullscreenButtonFocus()).catch((err) => {
        debugLog.emit("RENDER", `⚠️ 进入全屏失败：${err?.message || err}`);
      });
    } else {
      releaseFullscreenButtonFocus();
    }
  }, [releaseFullscreenButtonFocus]);

  const handleEnterFullscreen = () => {
    if (document.fullscreenElement) {
      const p = document.exitFullscreen?.();
      if (p?.then) {
        p.then(() => releaseFullscreenButtonFocus()).catch(() => {});
      } else {
        releaseFullscreenButtonFocus();
      }
      return;
    }
    enterStageFullscreen();
  };

  /* ─── 渲染 ─── */
  const centerRoleRefKey = centerAgentEntry
    ? `agent:${centerAgentEntry.agentId || getAgentName(centerAgentEntry)}`
    : centerMemberEntry
      ? `member:${centerMemberEntry.userId || getMemberName(centerMemberEntry)}`
      : null;

  /* 发言者滚入视野：10 人以上时成员条要横向滚动，正在发言的人可能在可视区外，
   * 高亮就白做了。只滚成员条自身（inline/block 都取 nearest），不影响任何其他滚动位置。 */
  useEffect(() => {
    const strip = humanStripRef.current;
    if (!strip) return;
    strip.querySelector(".expo-human-chip.speaking")
      ?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [recentMemberKey, selfSpeechPhase, speakingMemberIds]);

  // 有队列头 → 当前正在/即将发言 → 数字人激活；无队列头 → 静默 fallback
  const centerIsPending = !!queueHead;
  // 人类占据中央时名字也要跟着换，否则会出现"头像是我、名字是上一位 AI"
  const speakerName = centerHumanEntry
    ? getMemberName(centerHumanEntry)
    : centerAgentEntry
      ? getAgentName(centerAgentEntry)
      : "";
  /* 综合模式花名册列数：1 人单列居中；2 / 4 人两列（4 人成 2×2）；其余三列。 */
  const rosterCount = (virtualRoster || []).length;
  const rosterCols = rosterCount <= 1 ? 1 : (rosterCount === 2 || rosterCount === 4) ? 2 : 3;
  const speakerNameEl = speakerName ? (
    <div className="expo-center-name">
      {speakerName}
      {queueHead?.embodied ? <span className="expo-x2-badge">由 X2 发言</span> : null}
    </div>
  ) : null;
  const subtitleBar = (
    <div
      className={`expo-subtitle-bar ${selfSpeechPhase !== "idle" || silentGuide ? "self-speech" : ""} ${isStatusHint ? "status-hint" : ""} ${silentGuide ? `silent-guide ${silentGuide.kind || "natural_continue"}` : ""} ${!subtitleText && selfSpeechPhase !== "waiting" ? "empty" : ""} ${selfSpeechPhase === "waiting" ? "typing" : ""} ${canExpandSpeechDetail ? "clickable" : ""}`}
      aria-live="polite"
      role={canExpandSpeechDetail ? "button" : undefined}
      tabIndex={canExpandSpeechDetail ? 0 : undefined}
      title={canExpandSpeechDetail ? "点击展开发言详情" : undefined}
      onClick={() => {
        if (canExpandSpeechDetail) setSpeechDetailOpen((v) => !v);
      }}
      onKeyDown={(e) => {
        if (!canExpandSpeechDetail) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setSpeechDetailOpen((v) => !v);
        }
      }}
    >
      {subtitlePrefix && <span className="expo-subtitle-prefix">{subtitlePrefix}</span>}
      {selfSpeechPhase === "waiting" || selfSpeechPhase === "arming" || selfSpeechPhase === "recording" || selfSpeechPhase === "recognizing" || silentGuide
        // 状态提示和逐字浮现不能过 markdown：短句会被拆成 <p> 后逐字换行；
        // 未闭合的引号/标记也会被解析成别的东西。
        ? <span className="expo-subtitle-text">{subtitleText}</span>
        : subtitleText && <ReactMarkdown>{subtitleText}</ReactMarkdown>}
    </div>
  );

  const startFullscreenOpt = (
    <label className="expo-preload-fullscreen-opt">
      <input
        type="checkbox"
        checked={startInFullscreen}
        onChange={(e) => setStartInFullscreen(e.target.checked)}
      />
      开始后进入全屏
    </label>
  );

  return (
    <div
      ref={stageRootRef}
      className={`science-expo-stage ${layoutVariant === "comprehensive" ? "science-expo-stage--comprehensive" : ""} ${isFullscreen ? "is-fullscreen" : ""}`}
      tabIndex={-1}
      style={brandThemeStyle || undefined}
    >
      <div className="expo-orb expo-orb-left" />
      <div className="expo-orb expo-orb-right" />

      {/* ── Header ── */}
      <header className="expo-stage-header">
        <div className={`expo-brand-block ${isFullscreen ? "expo-brand-block--org" : ""}`}>
          {isFullscreen ? (
            <>
              <img
                src={orgBrand.logoUrl}
                alt="Logo"
                className="expo-logo"
              />
              <div className="expo-brand-copy">
                <h1 className="expo-org-title" title={orgBrand.title}>{orgBrand.title}</h1>
                {(orgBrand.schoolName || orgBrand.showPlatformMark) && (
                  <div className="expo-brand-meta-row">
                    {orgBrand.schoolName ? (
                      orgBrand.schoolNameType === "image" ? (
                        <img
                          className="expo-school-name expo-school-name-image"
                          src={orgBrand.schoolNameSrc}
                          alt="学校名称"
                        />
                      ) : (
                        <span className="expo-school-name" title={orgBrand.schoolName}>
                          {orgBrand.schoolName}
                        </span>
                      )
                    ) : null}
                    {orgBrand.showPlatformMark && (
                      <span className="expo-platform-mark" title="邮百工" aria-label="邮百工">
                        <img src="/avatars/mozi.png" alt="邮百工" draggable={false} />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <h1>AI 科学圆桌</h1>
          )}
        </div>
        <div className="expo-topic-block">
          <div className="expo-topic-title">
            <span className="expo-topic-title-main">{projectTitleParts.title || projectName || "科学议题展示"}</span>
            {projectTitleParts.creator && (
              <span className="expo-topic-title-creator">创建者：{projectTitleParts.creator}</span>
            )}
          </div>
          {/* 阶段流：所有阶段平铺，高亮当前；溢出可横滚（多阶段长名兜底）。 */}
          {phases && phases.length > 0 ? (
            <div className="expo-phase-track" role="tablist" aria-label="训练阶段">
              {phases.map((p, i) => {
                const active = i === phaseIndex;
                const passed = i < phaseIndex;
                return (
                  <span
                    key={p?.phaseId || p?.id || p?.name || i}
                    className={`expo-phase-chip ${active ? "active" : ""} ${passed ? "passed" : ""}`}
                    role="tab"
                    aria-selected={active}
                    title={p?.description || p?.name || ""}
                  >
                    <span className="expo-phase-chip-idx">{i + 1}</span>
                    <span className="expo-phase-chip-name">{p?.name || `阶段 ${i + 1}`}</span>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="expo-topic-meta">
              {phaseLabel && <span>{phaseLabel}</span>}
              <span>阶段 {progressText}</span>
              {(status === "creating" || status === "joining") && <span>启动中</span>}
              {isDisconnected && <span className="expo-status-warning">连接待恢复</span>}
            </div>
          )}
          {(isDisconnected || status === "creating" || status === "joining") && (
            <div className="expo-topic-meta">
              {(status === "creating" || status === "joining") && <span>启动中</span>}
              {isDisconnected && <span className="expo-status-warning">连接待恢复</span>}
            </div>
          )}
        </div>
        <div className="expo-header-actions">
          <label
            className="expo-volume-control"
            title={`数字人音量：${digitalHumanVolumePercent}%（已启用峰值保护）`}
          >
            <span className="expo-volume-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                <path d="M18 6a8.5 8.5 0 0 1 0 12" />
              </svg>
            </span>
            <span className="expo-volume-label">数字人音量</span>
            <input
              type="range"
              min={MIN_DIGITAL_HUMAN_VOLUME_PERCENT}
              max={MAX_DIGITAL_HUMAN_VOLUME_PERCENT}
              step="10"
              value={digitalHumanVolumePercent}
              onChange={(event) => setDigitalHumanVolumePercent(
                clampDigitalHumanVolumePercent(event.target.value)
              )}
              aria-label="数字人音量"
              aria-valuetext={`${digitalHumanVolumePercent}%`}
              style={{ "--expo-volume-level": `${(digitalHumanVolumePercent - MIN_DIGITAL_HUMAN_VOLUME_PERCENT) / (MAX_DIGITAL_HUMAN_VOLUME_PERCENT - MIN_DIGITAL_HUMAN_VOLUME_PERCENT) * 100}%` }}
            />
            <output>{digitalHumanVolumePercent}%</output>
          </label>
          <button
            type="button"
            className="expo-icon-btn expo-fullscreen-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleEnterFullscreen}
            title={isFullscreen ? "退出全屏" : "全屏"}
          >{isFullscreen ? "❐" : "⛶"}</button>
          {typeof onClose === "function" && (
            <button
              type="button"
              className="expo-icon-btn expo-close-btn"
              onClick={onClose}
              title="关闭"
            >×</button>
          )}
        </div>
      </header>

      {/* ── 三栏主区 ── */}
      <main className="expo-stage-main">

        {/* 左栏：花名册候场 */}
        <section className="expo-stage-left">
          {/* 数字人区：显示全部角色，当前阶段参与者高亮；超出三行可滚动 */}
          <div
            className="expo-roster-virtual"
            data-roster-count={rosterCount}
            data-roster-cols={rosterCols}
            style={{ "--expo-roster-cols": rosterCols }}
          >
            {virtualRoster.map((agent) => {
              const agentId    = agent.agentId || agent.id;
              const roleRefKey = `agent:${agentId || getAgentName(agent)}`;
              const isSpeaking = centerRoleRefKey === roleRefKey;
              // 已被点到、正在生成发言，但还没轮到出声：先在花名册上亮起来，
              // 让观众在静默期就知道该看谁
              const isThinking = !isSpeaking && thinkingAgent?.agentId === agentId;
              const pendingCount = pendingCountByAgent.get(agentId) || 0;
              const isStageActive = !hasStageActiveInfo
                || isRecordingScriptMode
                || stageActiveAgentKeySet.has(String(agentId || ""))
                || stageActiveAgentKeySet.has(getAgentName(agent));
              return (
                <div
                  key={roleRefKey}
                  className={`expo-seat-card virtual ${agent._isHost ? "host" : ""} ${isSpeaking ? "speaking" : ""} ${isThinking ? "thinking" : ""} ${pendingCount > 0 ? "has-pending" : ""} ${isStageActive ? "stage-active" : "stage-inactive"}`}
                  title={isThinking
                    ? `${getAgentName(agent)}：正在组织发言`
                    : isStageActive ? `${getAgentName(agent)}：参与本阶段讨论` : `${getAgentName(agent)}：非本阶段讨论角色`}
                >
                  <div className="expo-seat-avatar virtual">
                    {digitalHumanEnabled && (!agent._isHost || hostConfigReady) ? (
                      <ScienceExpoDigitalHuman
                        isOpen
                        expertName={getAgentName(agent)}
                        text=""
                        variant="seat"
                        active={false}
                        messageType="science_expo_seat"
                        workflowName={projectWorkflowName}
                        configOverride={agent._isHost ? agent.digitalHumanConfig : null}
                        currentUser={currentUser}
                      />
                    ) : (
                      <span className="expo-seat-initial">{getAgentName(agent).slice(0, 1)}</span>
                    )}
                  </div>
                  {pendingCount > 0 && (
                    <span
                      className="expo-seat-queue-badge"
                      title={`${getAgentName(agent)} 还有 ${pendingCount} 句待说`}
                      aria-label={`${pendingCount} 句待说`}
                    >
                      {pendingCount > 9 ? "9+" : pendingCount}
                    </span>
                  )}
                  {isStageActive && !isSpeaking && (
                    <span className="expo-seat-stage-dot" aria-label="参与本阶段讨论" />
                  )}
                  <div className="expo-seat-name">{getAgentName(agent)}</div>
                </div>
              );
            })}
          </div>

          {/* 分割线（仅当有人类成员时） */}
          {members.length > 0 && <div className="expo-roster-divider" />}

          {/* 人类区（最多 4 个，1 行） */}
          <div className="expo-roster-human">
            {members.slice(0, 4).map((member) => {
              const roleRefKey = `member:${member.userId || getMemberName(member)}`;
              // centerRoleRefKey 只在 ASR 出结果、消息回流后才指向该成员；
              // 追加实时信号，让经典模式也能在按下麦克风的瞬间亮起来
              const isSpeaking = centerRoleRefKey === roleRefKey
                || (!!member.userId && !!speakingMemberIds?.has(member.userId));
              const isSelf     = member.userId === myUserId;
              return (
                <div
                  key={roleRefKey}
                  className={`expo-seat-card human ${isSpeaking ? "speaking" : ""} ${isSelf ? "self" : ""}`}
                >
                  <div className="expo-seat-avatar human">
                    <div className="expo-human-badge">
                      <span>{getMemberTitle(member) || getMemberName(member).slice(0, 2)}</span>
                    </div>
                  </div>
                  <div className="expo-seat-name">{getMemberName(member)}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* 中栏：当前发言
            P3 多数字人共存：spotlight rack（N 个数字人叠层）始终 mount 在 .expo-center-card
            内部，占据原 .expo-center-avatar 的位置；name 作为 flex 末项在它下方，
            布局与改造前完全一致。idle/human 发言时 rack 内所有实例 active=false 全 hidden，
            上方叠 idle dots / human badge 即可。 */}
        <section className="expo-stage-center">
          <div className={`expo-center-card ${centerHumanEntry ? "human" : ""}`}>
            {/* spotlight rack：始终渲染，永不卸载，保留 GPU 就绪态 */}
            {digitalHumanEnabled && (
              <div className="expo-spotlight-rack">
                {/* 有机构背景图时显示：椭圆柔光衬底，遮发丝抠图透底 */}
                <div className="expo-spotlight-backdrop" aria-hidden="true" />
                {virtualRoster.map((agent) => {
                  const agentId = agent.agentId || agent.id || getAgentName(agent);
                  if (agent._isHost && !hostConfigReady) return null;
                  const roleKey = `agent:${agentId}`;
                  const centerId = centerAgentEntry
                    ? (centerAgentEntry.agentId || centerAgentEntry.id || getAgentName(centerAgentEntry))
                    : null;
                  // active 跟 centerAgentEntry（centerLock 兜底），不依赖 centerIsPending：
                  // 用户按住说话清空队列时，上一位仍可见（静止安静站着），等下一位 queueHead
                  // 出现时再切换 → 避免"按住说话立即变空"的视觉跳跃。
                  // 预载遮罩期间强制全体 inactive：active=true 会启动该实例的 RAF
                  // 渲染循环和黑底抠图（逐帧读画布像素，纯主线程），跟形象解析抢
                  // 主线程。实测主持人一入队变 active，后续解析就从 0.16s 掉到 20s。
                  // 人类占据中央时全体让位：自己说话那会儿 centerLock 还停在上一位 AI，
                  // 不加这条就会让那位 AI 的形象压在人类头像上面。
                  const isActive = !!centerAgentEntry && centerId === agentId && !holdStage && !centerHumanEntry;
                  // 遮罩期间和"就绪待开场"期间都不下发文本 → 谁都不会开口，
                  // 队列原地等着，闸门一撤主持人立刻从头说完整的开场白。
                  const canSpeak = isActive && queueHead && !holdStage;
                  const text = canSpeak ? queueHead.voice : "";
                  const speechKey = canSpeak ? queueHead.key : "";
                  // lazy mount 准入：在 mountedAgentIds 中才允许 new ExpertAvatar。
                  // active=true 时强制放行（保险，防止 set 同步晚一拍把当前讲话角色拦下来）。
                  const mountReady =
                    isActive ||
                    mountedAgentIds.has(String(agentId)) ||
                    mountedAgentIds.has(getAgentName(agent));
                  return (
                    <ScienceExpoDigitalHuman
                      key={roleKey}
                      isOpen
                      expertName={getAgentName(agent)}
                      text={text}
                      speechKey={speechKey}
                      silent={!!(canSpeak && queueHead.embodied)}
                      variant="spotlight"
                      active={isActive}
                      mountReady={mountReady}
                      messageType="science_expo"
                      workflowName={projectWorkflowName}
                      configOverride={agent._isHost ? agent.digitalHumanConfig : null}
                      currentUser={currentUser}
                      controlRef={getSpotlightControlRef(roleKey)}
                      onPlaybackStart={() => {
                        setAiTransitioning(false);
                        debugLog.emit("SPEAKER", `▶️ 开始播放：${getAgentName(agent)}`);
                      }}
                      onPlaybackEnd={advanceQueue}
                    />
                  );
                })}

                {/* 人类发言时叠同一张成员头像；文件未就绪/加载失败时保留原文字徽章兜底 */}
                {centerHumanEntry && (
                  <div className="expo-center-avatar human overlay-human">
                    <div className="expo-center-human-portrait">
                      <div className="expo-human-badge large">
                        <span>{getMemberTitle(centerHumanEntry) || getMemberName(centerHumanEntry).slice(0, 2)}</span>
                      </div>
                      {centerHumanAvatarUrl && (
                        <img
                          className="expo-center-human-photo"
                          src={centerHumanAvatarUrl}
                          alt={getMemberName(centerHumanEntry)}
                          onError={(event) => { event.currentTarget.hidden = true; }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* idle 时叠 dots */}
                {!centerAgentEntry && !centerHumanEntry && (
                  <div className="expo-center-idle overlay-idle">
                    <div className="expo-center-idle-dot" />
                    <div className="expo-center-idle-dot" />
                    <div className="expo-center-idle-dot" />
                  </div>
                )}
              </div>
            )}

            {layoutVariant !== "comprehensive" && speakerNameEl}
          </div>
          {layoutVariant === "comprehensive" && (
            <div className="expo-speech-card">
              {speakerNameEl}
              {subtitleBar}
            </div>
          )}
        </section>

        {/* 右栏：对话流面板（阶段名/进度条已迁移到顶部 expo-phase-track） */}
        <section className="expo-stage-right">
          <div className="expo-info-panel">
            {/* ───── 现场成员条 ─────
             * 综合模式左栏被虚拟角色占满（.expo-roster-human 在该模式下隐藏），
             * 人类参与者原本无处可见。这里横向列出，讲话时高亮。
             * 高亮分两路：他人走 recentMemberKey（短窗口，到点自动熄灭），
             * 自己走 selfSpeechPhase——成员发言同步刻意跳过自己（不顶替中央数字人），
             * 所以中央讲话人锁定永远不会指向自己。 */}
            {layoutVariant === "comprehensive" && members.length > 0 && (
              <div className="expo-human-strip" ref={humanStripRef} aria-label="现场参与成员">
                {members.map((member) => {
                  const roleRefKey = `member:${member.userId || getMemberName(member)}`;
                  const isSelf = !!myUserId && member.userId === myUserId;
                  const isSpeaking = isSelf
                    ? (selfSpeechPhase !== "idle" && selfSpeechPhase !== "waiting")
                    // 他人：实时信号优先（按下即亮），recentMemberKey 作为信号丢失时的兜底
                    : ((!!member.userId && !!speakingMemberIds?.has(member.userId))
                      || recentMemberKey === roleRefKey);
                  const offline = member.isOnline === false;
                  const name = getMemberName(member);
                  const roleTitle = getMemberTitle(member);
                  const avatarUrl = memberAvatarMap.get(roleRefKey);
                  const tip = [
                    name,
                    roleTitle,
                    isSelf ? "本人" : "",
                    offline ? "已离线" : "在线",
                    isSpeaking ? "正在发言" : "",
                  ].filter(Boolean).join(" · ");
                  return (
                    <div
                      key={roleRefKey}
                      className={`expo-human-chip ${isSpeaking ? "speaking" : ""} ${isSelf ? "self" : ""} ${offline ? "offline" : ""}`}
                      title={tip}
                    >
                      <span className="expo-human-chip-avatar" aria-hidden="true">
                        <span className="expo-human-chip-initial">{name.slice(0, 1)}</span>
                        {avatarUrl && (
                          <img
                            className="expo-human-chip-photo"
                            src={avatarUrl}
                            alt=""
                            onError={(event) => { event.currentTarget.hidden = true; }}
                          />
                        )}
                        <span className="expo-human-chip-dot" />
                      </span>
                      <span className="expo-human-chip-name">{name}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ───── 对话流（替代原 latestSummary 单卡片） ─────
             * 时间序展示所有发言、人类按住按钮发言、阶段总结。自动滚到底，
             * 用户上滚后暂停并显示"↓ 最新"按钮。 */}
            <div className="expo-feed-wrap">
              <div className="expo-feed-head">
                <div className="expo-info-label">对话流</div>
                <div className="expo-feed-count">{feedItems.length} 条</div>
              </div>
              {feedItems.length === 0 ? (
                <div className="expo-info-placeholder">
                  <span>讨论尚未开始</span>
                  <span>所有发言、阶段总结将在此按时间展示</span>
                </div>
              ) : (
                <div className="expo-feed-scroll-wrap">
                  <div
                    className="expo-feed-scroll"
                    ref={feedScrollRef}
                    onScroll={handleFeedScroll}
                    onWheel={markFeedGesture}
                    onTouchStart={markFeedGesture}
                    onPointerDown={markFeedGesture}
                  >
                    <div className="expo-feed-scroll-inner" ref={feedContentRef}>
                    {feedHidden > 0 && (
                      <div className="expo-feed-trim">已隐藏更早的 {feedHidden} 条</div>
                    )}
                    {feedVisible.map((item) => {
                      const ts = item.timestamp
                        ? `${String(item.timestamp.getHours()).padStart(2, "0")}:${String(item.timestamp.getMinutes()).padStart(2, "0")}`
                        : "";
                      if (item.kind === "summary") {
                        return (
                          <div key={item.key} className="expo-feed-item kind-summary">
                            <div
                              className="expo-feed-summary-card clickable"
                              role="button"
                              tabIndex={0}
                              onClick={() => setFeedDetail(item)}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFeedDetail(item); } }}
                              title="点击查看完整阶段总结"
                            >
                              <div className="expo-feed-summary-head">
                                <span className="expo-feed-summary-badge">▣ {item.title}</span>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  {ts && <span className="expo-feed-time">{ts}</span>}
                                  {item.full && item.full !== item.markdown && (
                                    <span className="expo-feed-bubble-more">完整报告 ›</span>
                                  )}
                                </div>
                              </div>
                              <div className="expo-feed-summary-body">
                                <EnhancedMarkdown
                                  content={item.markdown}
                                  compactMode
                                  showCodeBlockHeader={false}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      }
                      if (item.kind === "system") {
                        return (
                          <div key={item.key} className="expo-feed-item kind-system">
                            <div className="expo-feed-system-text">{item.text}</div>
                            {ts && <span className="expo-feed-time">{ts}</span>}
                          </div>
                        );
                      }
                      if (item.kind === "read_receipt") {
                        return (
                          <div key={item.key} className="expo-feed-item kind-read-receipt">
                            <span className="expo-read-receipt-dot" aria-hidden="true">◉</span>
                            <span className="expo-read-receipt-label">{item.label}</span>
                            <span className="expo-read-receipt-detail">{item.detail}</span>
                            {ts && <span className="expo-feed-time">{ts}</span>}
                          </div>
                        );
                      }
                      // speech
                      const isSelf = item.senderType === "student" || item.senderType === "member";
                      const isHost = item.senderType === "host";
                      const sideClass = isSelf ? "side-self" : "side-other";
                      const roleClass = isHost ? "role-host" : isSelf ? "role-self" : "role-agent";
                      const previewUrl = !isSelf ? agentPreviewMap[item.senderName] : null;
                      const initial = (item.senderName || "?").trim().slice(0, 1);
                      return (
                        <div key={item.key} className={`expo-feed-item kind-speech ${sideClass} ${roleClass}`}>
                          <div className="expo-feed-avatar">
                            {isSelf ? (
                              <span className="expo-feed-avatar-icon" aria-label="人类发言">
                                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 2a4 4 0 0 0-4 4v5a4 4 0 0 0 8 0V6a4 4 0 0 0-4-4z" />
                                  <path d="M5 11a7 7 0 0 0 14 0" />
                                  <line x1="12" y1="18" x2="12" y2="22" />
                                </svg>
                              </span>
                            ) : previewUrl ? (
                              <img src={previewUrl} alt={item.senderName} loading="lazy" />
                            ) : (
                              <span className="expo-feed-avatar-fallback">{initial}</span>
                            )}
                          </div>
                          <div className="expo-feed-bubble-wrap">
                            <div className="expo-feed-meta">
                              <span className="expo-feed-name">{item.senderName || (isSelf ? "现场观众" : "虚拟角色")}</span>
                              {item.senderRole && !isSelf && (
                                <span className="expo-feed-role">{item.senderRole}</span>
                              )}
                              {ts && <span className="expo-feed-time">{ts}</span>}
                            </div>
                            <div
                              className={`expo-feed-bubble ${item.hasDetail ? "clickable" : ""}`}
                              {...(item.hasDetail ? {
                                role: "button",
                                tabIndex: 0,
                                onClick: () => setFeedDetail(item),
                                onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFeedDetail(item); } },
                                title: "点击查看完整内容",
                              } : {})}
                            >
                              <span className="expo-feed-bubble-tail" aria-hidden="true" />
                              {item.text}
                              {item.hasDetail && <span className="expo-feed-bubble-more">详情 ›</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {silentTurnNotice && (
                      <div
                        className={`expo-feed-guidance-card ${silentTurnNotice.kind || "natural_continue"}`}
                        role="status"
                        aria-live="polite"
                      >
                        <span className="expo-feed-guidance-icon" aria-hidden="true">!</span>
                        <span className="expo-feed-guidance-copy">
                          <strong>
                            {silentTurnNotice.kind === "low_information"
                              ? "需要再具体一点"
                              : "想让专家回应？"}
                          </strong>
                          <span>{silentTurnNotice.text}</span>
                        </span>
                      </div>
                    )}
                    </div>
                  </div>
                  {showJumpLatest && !jumpLatestInMicRow && (
                    <button
                      type="button"
                      className="expo-feed-jump"
                      onClick={() => scrollFeedToBottom(true)}
                      title="滚动到最新"
                    >
                      ↓ 最新
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 麦克风区（放在右栏底部）
           * .expo-mic-row 恒定占据与单独麦克风按钮完全相同的矩形：综合模式全屏下
           * "最新"出现时由麦克风按钮让出宽度，行不加高也不外扩；"最新"消失后
           * 麦克风按钮自动重新占满整行。 */}
          <div className="expo-mic-area">
            <div className="expo-mic-row">
              <button
                type="button"
                data-mic-hold="true"
                className={`expo-mic-hold ${isVoiceRecording ? "recording" : ""} ${isVoiceProcessing ? "processing" : ""} ${selfSpeechPhase === "arming" ? "arming" : ""}`}
                onMouseDown={startVoice}
                onMouseUp={stopVoice}
                onMouseLeave={(e) => { if (recordingStartedRef.current || pressDownTsRef.current) stopVoice(e); }}
                onTouchStart={startVoice}
                onTouchEnd={stopVoice}
                aria-pressed={isVoiceRecording}
                title="按住按钮或键盘空格说话，松开发送"
              >
                <span className="expo-mic-icon">🎙</span>
                <span className="expo-mic-copy">
                  <span className="expo-mic-text">
                    {isVoiceProcessing ? "语音识别中" : isVoiceRecording ? "松开结束发言" : "按住说话"}
                  </span>
                  <span className="expo-mic-hint">
                    {selfSpeechPhase === "arming"
                      ? "可以开始说话"
                      : isVoiceRecording
                        ? "正在聆听现场声音"
                        : isVoiceProcessing
                          ? "正在整理你的发言"
                          : "按住说话，松开发送"}
                  </span>
                </span>
              </button>
              {showJumpLatest && jumpLatestInMicRow && (
                <button
                  type="button"
                  className="expo-mic-jump"
                  onClick={() => scrollFeedToBottom(true)}
                  title="滚动到最新"
                >
                  <span aria-hidden="true">↓</span>
                  <span>最新</span>
                </button>
              )}
            </div>
            {robotBind?.bound && String(robotBind.speaker || "") === String(myUserId || "") && (
              <button
                type="button"
                className="expo-release-robot"
                onClick={() => onReleaseRobot?.()}
              >
                释放机器人
              </button>
            )}
            {voiceError && <div className="expo-voice-error">{voiceError}</div>}
          </div>

          {/* 舞台底边条：落地大屏最下方一条带在观众视线之外，最新消息和麦克风原本正好
           * 落在那里。这里占一段固定高度，把上面的 .expo-info-panel（flex:1）和麦克风
           * 整体顶出死区；带内只放看不清也不影响使用的信息。高度见 --expo-stage-footer-h。
           * 仅综合模式全屏显示，窗口开发环境不占高度。 */}
          {layoutVariant === "comprehensive" && (
            <div className="expo-stage-footer" aria-hidden="true">
              <div className="expo-stage-footer-plate">
                <div className="expo-stage-footer-brand">
                  {orgBrand.logoUrl && (
                    <img className="expo-stage-footer-logo" src={orgBrand.logoUrl} alt="" />
                  )}
                  <span className="expo-stage-footer-topic">{projectWorkflowName}</span>
                </div>
                <div className="expo-stage-footer-meta">
                  <span className="expo-stage-footer-phase-name">
                    {phaseLabel || currentPhase || "准备中"}
                  </span>
                  <span className="expo-stage-footer-progress">{progressText}</span>
                  <span className="expo-stage-footer-sep" />
                  <span>{members.filter((member) => member?.isOnline !== false).length} 人在线</span>
                  <span className={`expo-stage-footer-dot ${isDisconnected ? "off" : "on"}`} />
                </div>
              </div>
            </div>
          )}
        </section>

        {layoutVariant === "comprehensive" && (
          <section className="expo-capability-panel" aria-label="能力画像">
            <div className="expo-capability-panel-head">
              <h2>能力画像</h2>
            </div>
            <div className={`expo-capability-radar-placeholder ${competencyHasData ? "has-data" : ""}`}>
              <svg viewBox="8 6 244 214" role="img" aria-label="团队九维能力雷达图">
                <g className="expo-capability-domain-arcs">
                  {competencyGroups.map((group, index) => {
                    const startAngle = -150 + index * 120;
                    const endAngle = -30 + index * 120;
                    const label = radarLabelPoint(-90 + index * 120);
                    return (
                      <g key={group.name} className={`domain-${index + 1}`}>
                        <path d={radarArc(startAngle, endAngle)} />
                        <text x={label.x} y={label.y} textAnchor="middle" dy="0.35em">{group.name}</text>
                      </g>
                    );
                  })}
                </g>
                <g className="expo-capability-radar-grid">
                  {[1, 2, 3].map((ring) => (
                    <polygon
                      key={ring}
                      points={radarPolygon(COMPETENCY_DIMENSIONS.map(() => ring / 3))}
                    />
                  ))}
                  {COMPETENCY_DIMENSIONS.map((_, index) => {
                    const [x, y] = radarPoint(index, 1).split(",");
                    return <line key={index} x1="130" y1="130" x2={x} y2={y} />;
                  })}
                </g>
                {competencyHasData && (
                  <polygon className="expo-capability-radar-value" points={radarPolygon(competencyRatios)} />
                )}
              </svg>
            </div>
            <section className="expo-growth-panel" aria-label="能力维度">
              <div className="expo-growth-panel-head">
                <span>能力维度</span>
              </div>
              {competencyGroups.map((group) => {
                const growing = group.dimensions.some((dimension) => (dimension?.delta || 0) > 0);
                return (
                  <div className={`expo-growth-row ${growing ? "is-growing" : ""}`} key={group.name}>
                    <div className="expo-growth-segments">
                      {group.dimensions.map((dimension) => {
                        const score = Number(dimension?.score) || 0;
                        const delta = Number(dimension?.delta) || 0;
                        const lit = score > 0;
                        return (
                          <span
                            className={`${lit ? "is-lit" : ""} ${delta > 0 ? "is-gain" : ""}`}
                            key={dimension?.name}
                            title={lit
                              ? `${dimension.name}：累计 ${score}${delta > 0 ? `，本轮 +${delta}` : ""}`
                              : `${dimension?.name || ""}：尚未形成证据`}
                          >
                            <b>{dimension?.name}</b>
                            {lit && <strong>{score}</strong>}
                            {delta > 0 && <em>+{delta}</em>}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </section>
            <div className="expo-capability-records">
              <div className="expo-thinking-change">
                <span>思维变化</span>
                {recentEvidence.length > 0 ? (
                  <ol className="expo-thinking-change-list">
                    {recentEvidence.map((profile, index) => {
                      const current = index === 0;
                      const empty = profile.gains.length === 0;
                      return (
                        <li
                          className={`expo-thinking-change-item ${current ? "is-current" : "is-past"}${empty ? " is-empty" : ""}`}
                          key={profile.evaluatedSeq}
                        >
                          <i>{profile.turn || recentEvidence.length - index}</i>
                          <div className="expo-thinking-change-body">
                            <p>{profile.summary || "本轮未形成可累计证据。"}</p>
                            {current && profile.evidence && (
                              <blockquote>“{profile.evidence}”</blockquote>
                            )}
                            {current && !empty && (
                              <div className="expo-thinking-change-gains">
                                {profile.gains.map((dimension) => (
                                  <em key={dimension.name}>{dimension.name} +{dimension.delta}</em>
                                ))}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <p>等待学生围绕科学问题展开现场互动。</p>
                )}
              </div>
            </div>
            <div className="expo-capability-stats" aria-label="现场统计">
              <span><b>{members.filter((member) => member?.isOnline).length}</b> 人参与</span>
              <span><b>{studentRounds}</b> 次学生表达</span>
              <span>{phaseLabel || currentPhase || "准备中"}</span>
            </div>
          </section>
        )}
      </main>

      {/* 原展示模式保留全宽底部字幕条；综合展示模式已置于左上讲话人区。 */}
      {layoutVariant !== "comprehensive" && subtitleBar}

      {feedDetail && (
        // 借鉴字幕详情：非全屏遮罩、浮层 popover；点外侧关闭、ESC 关闭。
        <>
          <div className="expo-feed-detail-backdrop" onClick={() => setFeedDetail(null)} />
          <div className="expo-feed-detail-popover">
            <div className="expo-feed-detail-card">
              <div className="expo-feed-detail-head">
                <div className="expo-feed-detail-title">
                  {feedDetail.kind === "summary"
                    ? (feedDetail.title || "阶段小结")
                    : (feedDetail.senderName || "发言详情")}
                  {feedDetail.timestamp && (
                    <span className="expo-feed-detail-time">
                      {`${String(feedDetail.timestamp.getHours()).padStart(2, "0")}:${String(feedDetail.timestamp.getMinutes()).padStart(2, "0")}`}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="expo-feed-detail-close"
                  onClick={() => setFeedDetail(null)}
                  title="关闭（ESC）"
                >×</button>
              </div>
              <div className="expo-feed-detail-body">
                {feedDetail.kind === "summary" ? (
                  <EnhancedMarkdown
                    content={feedDetail.full || feedDetail.markdown}
                    compactMode
                    showCodeBlockHeader={false}
                  />
                ) : feedDetail.detail ? (
                  <EnhancedMarkdown
                    content={feedDetail.detail}
                    compactMode
                    disableMermaid
                    showCodeBlockHeader={false}
                  />
                ) : (
                  <p className="expo-feed-detail-text">{feedDetail.text}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {speechDetailOpen && activeDetailText && (
        <div className="expo-speech-detail-popover" onClick={() => setSpeechDetailOpen(false)}>
          <div className="expo-speech-detail-card" onClick={(e) => e.stopPropagation()}>
            <div className="expo-speech-detail-head">
              <span>发言详情</span>
              <button type="button" onClick={() => setSpeechDetailOpen(false)}>收起</button>
            </div>
            <div className="expo-speech-detail-body">
              <EnhancedMarkdown
                content={activeDetailText}
                compactMode
                disableMermaid
                showCodeBlockHeader={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── 调试面板（默认关，localStorage.expoDebug=1 或 Ctrl+Shift+D 切换） ── */}
      <ExpoDebugConsole />


      {/* ── 启动预载遮罩 ──
          全部形象就绪后才开演。热缓存下约 2s/个，15 个约 30s；换成没演示过的
          剧本时 zip 要冷下载（约 120MB），下载段单独显示。 */}
      {holdStage && (
        <div className="expo-preload-overlay">
          <div className="expo-preload-card">
            <div className="expo-kicker">
              {showHeldByPreload ? "正在准备圆桌" : "圆桌已就绪"}
            </div>
            <h2>{projectTitleParts.title || projectName || "AI 科学圆桌"}</h2>
            {showHeldByPreload ? (
              <>
                {zipProgress.total > 0 && zipProgress.done < zipProgress.total && (
                  <div className="expo-preload-row">
                    <span className="expo-preload-label">下载形象资源</span>
                    <div className="expo-preload-track">
                      <div
                        className="expo-preload-fill downloading"
                        style={{ width: `${(zipProgress.done / zipProgress.total) * 100}%` }}
                      />
                    </div>
                    <span className="expo-preload-count">{zipProgress.done}/{zipProgress.total}</span>
                  </div>
                )}
                <div className="expo-preload-row">
                  <span className="expo-preload-label">数字人就绪</span>
                  <div className="expo-preload-track">
                    <div
                      className="expo-preload-fill"
                      style={{ width: preloadTotal ? `${(preloadDone / preloadTotal) * 100}%` : "0%" }}
                    />
                  </div>
                  <span className="expo-preload-count">
                    {preloadTotal ? `${preloadDone}/${preloadTotal}` : "—"}
                  </span>
                </div>
                <p className="expo-preload-hint">
                  {preloadCurrent ? `正在就位：${preloadCurrent}` : "正在读取角色名单…"}
                </p>
                <p className="expo-preload-note">
                  全部角色就位后由你决定何时开场，不会自己开始。
                </p>
                {startFullscreenOpt}
              </>
            ) : (
              /* 就绪待开场：卡片原地换成「开始」，视线不用移动。
                 点击是用户手势，顺手在手势栈内 resume 共享 AudioContext，
                 杜绝主持人首段音频被浏览器自动播放策略拦掉。
                 勾了全屏也必须写在这个 click 里：Fullscreen API 要求用户手势，
                 不能等 setState 后再 effect 申请。 */
              <>
                <p className="expo-preload-hint">
                  准备好后点「开始」，主持人从头讲开场白。
                </p>
                {startFullscreenOpt}
                <div className="expo-preload-actions">
                  <button
                    type="button"
                    onClick={() => {
                      try { ensureSharedAudioContext(); } catch (_) {}
                      if (startInFullscreen) enterStageFullscreen();
                      debugLog.emit("RENDER", `▶️ 演示者点击「开始」，放行开场${startInFullscreen ? "（全屏）" : ""}`);
                      setStageStarted(true);
                    }}
                  >
                    开始
                  </button>
                </div>
                <p className="expo-preload-note">
                  在此之前台上不会有人开口，可以放心等观众到齐。
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── 完成遮罩 ── */}
      {status === "completed" && (
        <div className="expo-summary-overlay">
          <div className="expo-summary-card">
            <div className="expo-kicker">科学总结</div>
            <h2>本轮圆桌讨论已完成</h2>
            <p>AI 多角色围绕科学问题完成了观点提出、证据讨论和阶段性总结。</p>
            <div className="expo-summary-actions">
              <button onClick={onSaveArchive}>保存本轮展示记录</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScienceExpoStage;
